import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import axios from "axios";
import { PersonalDetailsTab, ArtistsTab, VenuesTab, FriendsTab, UsersTab } from './SettingsTabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://gigloader.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

// Authentication Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Axios interceptor to add token to requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          // Set the token state and axios header
          setToken(storedToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          // Token is invalid, remove it silently (don't show error to user)
          if (error.response?.status === 403 || error.response?.status === 401) {
            // Expected authentication error - handle silently
            console.debug('Invalid token removed from localStorage');
          } else {
            // Unexpected error - log it
            console.error('Unexpected auth error:', error);
          }
          localStorage.removeItem('token');
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []); // Only run on initial mount

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Set authorization header immediately for the next request
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get user info
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Band Member Display Component
const BandMemberDisplay = ({ gigId, bandMembers, onUpdate }) => {
  const [showSelector, setShowSelector] = useState(false);
  const [availableFriends, setAvailableFriends] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentBandMembers, setCurrentBandMembers] = useState(bandMembers || []);
  const [pendingSelections, setPendingSelections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const API_URL = `${BASE_URL}/api`;

  // Update current band members when props change
  useEffect(() => {
    setCurrentBandMembers(bandMembers || []);
  }, [bandMembers]);

  // Fetch available friends
  useEffect(() => {
    fetchFriends();
    if (gigId && bandMembers && bandMembers.length > 0) {
      fetchBandMemberDetails();
    }
  }, [gigId, bandMembers]);

  // Fetch band member details when IDs are provided
  const fetchBandMemberDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/gigs/${gigId}/band-members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const memberDetails = await response.json();
        console.log('Fetched band member details:', memberDetails);
        setCurrentBandMembers(memberDetails);
      } else {
        console.error('Failed to fetch band member details');
        // Fallback: if the IDs are actually objects already, use them
        if (bandMembers && bandMembers.length > 0 && typeof bandMembers[0] === 'object') {
          setCurrentBandMembers(bandMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching band member details:', error);
      // Fallback: if the IDs are actually objects already, use them
      if (bandMembers && bandMembers.length > 0 && typeof bandMembers[0] === 'object') {
        setCurrentBandMembers(bandMembers);
      }
    }
  };

  // Filter friends based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFriends(availableFriends);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = availableFriends.filter(friend =>
        friend.name.toLowerCase().includes(query) ||
        (friend.email && friend.email.toLowerCase().includes(query))
      );
      setFilteredFriends(filtered);
    }
  }, [searchQuery, availableFriends]);

  const fetchFriends = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/friends`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter out any friends without valid IDs or names
        const validFriends = data.filter(friend => 
          friend && 
          friend.id && 
          typeof friend.id === 'string' && 
          friend.name && 
          typeof friend.name === 'string'
        );
        console.log(`Loaded ${validFriends.length} valid friends out of ${data.length} total`);
        setAvailableFriends(validFriends);
        setFilteredFriends(validFriends); // Initialize filtered list
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
      setError('Failed to load friends');
    }
  };

  const handleAddBandMembers = async () => {
    if (!pendingSelections.length) {
      setError('Please select at least one band member');
      return;
    }
    
    // Filter out any selections without valid IDs
    const validSelections = pendingSelections.filter(f => f && f.id && typeof f.id === 'string');
    
    if (!validSelections.length) {
      setError('Selected band members have invalid IDs. Please try again.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const bandMemberIds = validSelections.map(f => f.id);
      
      console.log('Saving band members:', bandMemberIds);
      console.log('Valid selections:', validSelections);
      
      const response = await fetch(`${API_URL}/gigs/${gigId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          band_members: bandMemberIds
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Band members saved successfully:', responseData);
        
        // Update the display immediately with the selected friends (full objects)
        setCurrentBandMembers(validSelections);
        
        // Clear pending selections and close selector
        setPendingSelections([]);
        setShowSelector(false);
        
        // Call onUpdate to notify parent (but don't reload page)
        onUpdate();
      } else {
        const errorData = await response.text();
        console.error('API Error:', response.status, errorData);
        setError(`Failed to save band members: ${response.status} ${errorData}`);
      }
    } catch (error) {
      console.error('Error updating band members:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeBandMember = async (friendId) => {
    const updatedMembers = currentBandMembers.filter(f => f.id !== friendId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/gigs/${gigId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          band_members: updatedMembers.map(f => f.id)
        }),
      });
      
      if (response.ok) {
        setCurrentBandMembers(updatedMembers);
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing band member:', error);
      setError(`Failed to remove band member: ${error.message}`);
    }
  };

  const toggleFriendSelection = (friend) => {
    const isSelected = pendingSelections.some(f => f.id === friend.id);
    if (isSelected) {
      setPendingSelections(pendingSelections.filter(f => f.id !== friend.id));
    } else {
      setPendingSelections([...pendingSelections, friend]);
    }
  };

  const openSelector = () => {
    setPendingSelections([...currentBandMembers]); // Start with current selections
    setSearchQuery(''); // Reset search
    setError('');
    setShowSelector(true);
  };

  return (
    <div className="band-members-section">
      {currentBandMembers.length > 0 && (
        <div className="band-members-display">
          <div className="band-members-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <circle cx="16" cy="7" r="3"></circle>
            </svg>
            <span className="gig-detail-label">Band Members:</span>
          </div>
          <div className="band-members-list">
            {currentBandMembers.map(member => (
              <div key={member.id} className="band-member-item">
                {member.profile_image ? (
                  <img 
                    src={
                      member.profile_image.startsWith('/uploads/') 
                        ? `${BASE_URL}/api${member.profile_image}`
                        : member.profile_image.startsWith('/api/uploads/')
                        ? `${BASE_URL}${member.profile_image}`
                        : member.profile_image.startsWith('http')
                        ? member.profile_image
                        : `${BASE_URL}/api/uploads/${member.profile_image}`
                    } 
                    alt={member.name}
                    className="band-member-image"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="band-member-initials">
                    {member.initials || (member.name ? member.name.split(' ').map(n => n[0]).join('').toUpperCase() : '??')}
                  </div>
                )}
                <span className="band-member-name">{member.name || 'Unknown'}</span>
                <button 
                  className="remove-band-member"
                  onClick={() => removeBandMember(member.id)}
                  title="Remove band member"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button 
        className="btn btn-secondary add-band-members-btn"
        onClick={openSelector}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
          <circle cx="16" cy="7" r="3"></circle>
          <line x1="19" y1="8" x2="19" y2="14"></line>
          <line x1="22" y1="11" x2="16" y2="11"></line>
        </svg>
        Add Band Members
      </button>

      {showSelector && (
        <div className="band-member-selector">
          <div className="selector-header">
            <h4>Select Band Members</h4>
            <button 
              className="close-selector"
              onClick={() => setShowSelector(false)}
            >
              ×
            </button>
          </div>
          
          {/* Search Input */}
          <div className="search-section">
            <div className="search-input-container">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-icon">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search band members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-info">
                {filteredFriends.length} of {availableFriends.length} friends
              </div>
            )}
          </div>
          
          {error && (
            <div className="error-message" style={{color: 'red', marginBottom: '1rem', padding: '0.5rem', background: '#fef2f2', borderRadius: '4px'}}>
              {error}
            </div>
          )}
          
          <div className="friends-list">
            {filteredFriends.length > 0 ? (
              filteredFriends.map(friend => (
                <div 
                  key={friend.id} 
                  className={`friend-item ${pendingSelections.some(f => f.id === friend.id) ? 'selected' : ''}`}
                  onClick={() => toggleFriendSelection(friend)}
                >
                  {friend.profile_image ? (
                    <img 
                      src={
                        friend.profile_image.startsWith('/uploads/') 
                          ? `${BASE_URL}/api${friend.profile_image}`
                          : friend.profile_image.startsWith('/api/uploads/')
                          ? `${BASE_URL}${friend.profile_image}`
                          : friend.profile_image.startsWith('http')
                          ? friend.profile_image
                          : `${BASE_URL}/api/uploads/${friend.profile_image}`
                      } 
                      alt={friend.name}
                      className="friend-image"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="friend-initials">
                      {friend.name ? friend.name.split(' ').map(n => n[0]).join('').toUpperCase() : '??'}
                    </div>
                  )}
                  <div className="friend-info">
                    <span className="friend-name">{friend.name || 'Unknown'}</span>
                    {friend.email && <span className="friend-email">{friend.email}</span>}
                  </div>
                  {pendingSelections.some(f => f.id === friend.id) && (
                    <div className="selection-indicator">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">
                {searchQuery ? (
                  <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <p>No friends found matching "{searchQuery}"</p>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => setSearchQuery('')}
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                    </svg>
                    <p>No friends available</p>
                    <p className="no-results-subtitle">Add friends in Settings to include them as band members</p>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="selector-actions">
            <button 
              className="btn btn-primary"
              onClick={handleAddBandMembers}
              disabled={loading || !pendingSelections.length}
            >
              {loading ? 'Saving...' : `Save Band Members (${pendingSelections.length})`}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowSelector(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// AutoComplete Component for Artists/Venues
const AutocompleteInput = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder, 
  searchEndpoint, 
  createEndpoint, 
  displayField = 'name',
  className = '',
  label,
  icon,
  onItemCreated  // New callback for when items are created
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

  const searchItems = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}${searchEndpoint}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const createNewItem = async (name) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}${createEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      if (response.ok) {
        const newItem = await response.json();
        onSelect(newItem[displayField]);
        setShowSuggestions(false);
        
        // Notify parent component that a new item was created
        if (onItemCreated) {
          onItemCreated(newItem);
        }
      }
    } catch (error) {
      console.error('Create error:', error);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for search
    const timeout = setTimeout(() => {
      searchItems(newValue);
      setShowSuggestions(true);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleSuggestionClick = (item) => {
    onSelect(item[displayField]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleCreateNew = () => {
    createNewItem(value);
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Check if current value exactly matches any suggestion
  const exactMatch = suggestions.find(item => 
    item[displayField].toLowerCase() === value.toLowerCase()
  );

  return (
    <div className="autocomplete-container">
      {label && (
        <label className="form-label-inline">
          {icon}
          {label}
        </label>
      )}
      <div className="autocomplete-input-wrapper">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={`form-input ${className}`}
          required
        />
        
        {showSuggestions && (
          <div className="autocomplete-suggestions">
            {loading && (
              <div className="autocomplete-item loading">
                Searching...
              </div>
            )}
            
            {suggestions.map((item, index) => (
              <div
                key={item.id || index}
                className="autocomplete-item"
                onClick={() => handleSuggestionClick(item)}
              >
                {item[displayField]}
              </div>
            ))}
            
            {!loading && suggestions.length === 0 && value.length >= 2 && (
              <>
                <div className="autocomplete-item no-results">
                  No matches found
                </div>
                <div
                  className="autocomplete-item create-new"
                  onClick={handleCreateNew}
                >
                  + Add new: "{value}"
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Login Component
const LoginForm = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Client-side validation
    if (!credentials.username.trim()) {
      setError('Username is required');
      setLoading(false);
      return;
    }
    
    if (!credentials.password.trim()) {
      setError('Password is required');
      setLoading(false);
      return;
    }
    
    const result = await login(credentials.username, credentials.password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>
            <svg width="32" height="32" viewBox="0 0 3000 3000" fill="none" xmlns="http://www.w3.org/2000/svg" className="login-header-icon">
              <path d="M2754.9,411.93h-355.94v-121.75c0-114.74-93.34-208.08-208.08-208.08h-111.58c-114.74,0-208.08,93.35-208.08,208.08v121.75h-742.43v-121.75c0-114.74-93.35-208.08-208.08-208.08h-111.58c-114.74,0-208.08,93.35-208.08,208.08v121.75H245.1c-39.01,0-70.74,31.74-70.74,70.74v2230.08c0,39.01,31.73,70.74,70.74,70.74h2509.79c39.01,0,70.74-31.73,70.74-70.74V482.67c0-39.01-31.73-70.74-70.74-70.74ZM809.12,883.99h111.58c114.74,0,208.08-93.34,208.08-208.08v-122.49h742.43v122.49c0,114.74,93.34,208.08,208.08,208.08h111.58c114.74,0,208.08-93.34,208.08-208.08v-122.49h285.19v2088.6H315.84V553.42h285.19v122.49c0,114.74,93.34,208.08,208.08,208.08ZM2079.3,223.59h111.58c36.72,0,66.6,29.88,66.6,66.6v385.72c0,36.72-29.88,66.6-66.6,66.6h-111.58c-36.72,0-66.6-29.88-66.6-66.6v-385.72c0-36.72,29.88-66.6,66.6-66.6ZM809.12,223.59h111.58c36.72,0,66.6,29.88,66.6,66.6v385.72c0,36.72-29.88,66.6-66.6,66.6h-111.58c-36.72,0-66.6-29.88-66.6-66.6v-385.72c0-36.72,29.88-66.6,66.6-66.6Z" fill="currentColor"/>
              <path d="M546.01,2101.99c-23.29,38.99-38.12,80.68-44.08,123.89-6.29,45.56-2.4,90.62,11.55,133.96,13.95,43.33,37.09,82.2,68.77,115.54,30.05,31.62,66.42,56.82,108.08,74.91,46.25,20.08,95.95,30.25,147.75,30.25,41.28,0,82.58-6.56,122.75-19.49,71.76-23.1,134.22-64.72,180.62-120.36,12.18-14.6,23.21-30.14,32.79-46.18,23.29-39,38.12-80.68,44.08-123.89,1.43-10.38,2.34-21.02,2.71-31.63.24-6.82.24-13.68.02-20.39v-686.86l822.96-175.09-.6,518.7-.22,199.44c-2.75,25.98-11.51,51.48-26.05,75.82-29.01,48.58-77.06,85.66-135.3,104.41-25.95,8.36-52.57,12.59-79.12,12.59-44.44,0-86.08-11.69-120.42-33.81-34.02-21.91-58.14-52.57-69.77-88.66-31.62-98.22,37.75-208.73,154.63-246.36,25.43-8.19,51.44-12.45,77.31-12.67h1.04c.35-.02.7-.02,1.05-.02,20.54,0,40.77,2.6,60.14,7.73l43.96,11.65v-417.79c0-39.01-31.74-70.74-70.74-70.74s-70.74,31.73-70.74,70.74v258.72c-28.76,2.76-57.38,8.69-85.37,17.7-90.97,29.29-166.77,88.43-213.41,166.54-23.29,39-38.12,80.68-44.08,123.89-6.29,45.56-2.4,90.63,11.55,133.96,13.95,43.33,37.09,82.2,68.77,115.54,30.05,31.62,66.42,56.82,108.08,74.91,46.25,20.08,95.96,30.25,147.74,30.25,41.28,0,82.58-6.56,122.75-19.49,71.19-22.92,133.29-64.1,179.58-119.1,12.58-14.95,23.96-30.91,33.83-47.43,23.29-39,38.12-80.68,44.08-123.9.76-5.54,1.39-11.18,1.85-16.76,1.34-16.07,1.39-32.3.17-48.26l.87-764.92c.02-21.46-9.53-41.52-26.2-55.03-16.66-13.51-38.26-18.7-59.27-14.24l-964.54,205.21c-32.46,6.91-56.02,36.01-56.02,69.19v761.72c-1.32,29.36-10.34,58.22-26.8,85.78-29.01,48.58-77.06,85.66-135.3,104.41-25.95,8.35-52.57,12.59-79.12,12.59-44.44,0-86.08-11.69-120.42-33.81-34.02-21.91-58.15-52.57-69.77-88.66-31.62-98.22,37.75-208.73,154.63-246.36,26.16-8.42,52.87-12.69,79.4-12.69,2.89,0,6.04.07,9.62.21,21.41.85,42.3,4.52,62.08,10.93l45.78,14.82v-742.37l1130.62-252.84c38.07-8.51,62.11-46.41,53.6-84.48-8.51-38.06-46.41-62.1-84.48-53.6l-1185.92,265.21c-32.56,7.28-55.3,35.67-55.3,69.04v632.35c-33.24,2.04-66.36,8.31-98.75,18.74-90.98,29.29-166.77,88.43-213.42,166.54h0Z" fill="currentColor"/>
            </svg>
            Gig Manager
          </h1>
          <p>Sign in to manage your gigs</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              placeholder="Enter your username"
            />
          </div>
          
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              placeholder="Enter your password"
            />
          </div>
          
          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="login-footer">
          <button 
            className="footer-link"
            onClick={() => setShowPrivacyModal(true)}
          >
            Privacy Policy
          </button>
          <span className="footer-separator">•</span>
          <button 
            className="footer-link"
            onClick={() => setShowTermsModal(true)}
          >
            Terms of Service
          </button>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="modal legal-modal" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal-content legal-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Privacy Policy</h3>
              <button 
                className="modal-close"
                onClick={() => setShowPrivacyModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="modal-body legal-modal-body">
              <div className="legal-content">
                <p><strong>Last updated:</strong> 24/8/25</p>
                
                <h2>1. Introduction</h2>
                <p>Mark Peric ("we," "us," or "our") operates the Gig Manager web application and website (the "Service"). This Privacy Policy informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.</p>
                
                <h2>2. Information We Collect</h2>
                <p>We collect several different types of information for various purposes to provide and improve our Service to you.</p>
                
                <h3>a. Personal Data</h3>
                <p>While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). This may include, but is not limited to:</p>
                <ul>
                  <li>Email address</li>
                  <li>First name and last name</li>
                  <li>Phone number</li>
                  <li>Payment information (collected by our payment processor, see Section 4)</li>
                  <li>Location data (if you provide it)</li>
                  <li>User-generated content (e.g., gig descriptions, notes)</li>
                </ul>
                
                <h3>b. Usage Data</h3>
                <p>We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer's Internet Protocol address (e.g., IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers, and other diagnostic data.</p>
                
                <h2>3. Use of Data</h2>
                <p>Gig Manager uses the collected data for various purposes:</p>
                <ul>
                  <li>To provide and maintain the Service</li>
                  <li>To notify you about changes to our Service</li>
                  <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
                  <li>To provide customer support</li>
                  <li>To monitor the usage of the Service</li>
                  <li>To detect, prevent, and address technical issues</li>
                  <li>To manage your account and provide you with a personalized user experience</li>
                  <li>To send you emails or notifications related to your gigs and app usage</li>
                </ul>
                
                <h2>4. Data Security</h2>
                <p>The security of your data is important to us. We use HTTPS encryption, data encryption at rest, and secure servers to protect your Personal Data. However, remember that no method of transmission over the Internet or method of electronic storage is 100% secure.</p>
                
                <h2>5. Third-Party Service Providers</h2>
                <p>We may employ third-party companies and individuals to facilitate our Service, provide the Service on our behalf, or assist us in analyzing how our Service is used. These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.</p>
                <ul>
                  <li><strong>Analytics:</strong> Google Analytics</li>
                  <li><strong>Email Sending:</strong> Gmail API</li>
                  <li><strong>Payment Processing:</strong> Stripe</li>
                </ul>
                
                <h2>6. Your Data Protection Rights</h2>
                <p>Depending on your location, you may have the following data protection rights:</p>
                <ul>
                  <li>The right to access, update or delete the information we have on you.</li>
                  <li>The right of rectification.</li>
                  <li>The right to object.</li>
                  <li>The right of restriction.</li>
                  <li>The right to data portability.</li>
                  <li>The right to withdraw consent.</li>
                </ul>
                
                <h2>7. Changes to This Privacy Policy</h2>
                <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.</p>
                
                <h2>8. Contact Us</h2>
                <p>If you have any questions about this Privacy Policy, please contact us:</p>
                <ul>
                  <li><strong>By email:</strong> <a href="mailto:mark@markperic.com">mark@markperic.com</a></li>
                </ul>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-primary"
                onClick={() => setShowPrivacyModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="modal legal-modal" onClick={() => setShowTermsModal(false)}>
          <div className="modal-content legal-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Terms of Service</h3>
              <button 
                className="modal-close"
                onClick={() => setShowTermsModal(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="modal-body legal-modal-body">
              <div className="legal-content">
                <p><strong>Last updated:</strong> 24/8/25</p>
                
                <h2>1. Introduction</h2>
                <p>Welcome to Gig Manager ("we," "us," or "our"). These Terms of Service ("Terms") govern your use of the Gig Manager web application and website (collectively, the "Service").</p>
                <p>By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you disagree with any part of the terms, then you may not access the Service.</p>
                
                <h2>2. Acceptance of Terms</h2>
                <p>Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the Service.</p>
                
                <h2>3. Changes to Terms</h2>
                <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
                <p>By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.</p>
                
                <h2>4. User Accounts</h2>
                <p>When you create an account with us, you must provide us with information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.</p>
                <p>You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</p>
                
                <h2>5. User Conduct and Responsibilities</h2>
                <p>You agree to use the Service only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the Service. Prohibited behavior includes harassing or causing distress or inconvenience to any other user, transmitting obscene or offensive content, or disrupting the normal flow of dialogue within the Service.</p>
                <p>You are responsible for all your activities in connection with the Service. This includes, but is not limited to:</p>
                <ul>
                  <li>Providing accurate information related to your gigs.</li>
                  <li>Respecting the privacy and data of other users.</li>
                  <li>Not using the Service for any illegal or unauthorized purpose.</li>
                  <li>Not trying to gain unauthorized access to our systems or other users' accounts.</li>
                </ul>
                
                <h2>6. User-Generated Content</h2>
                <p>Our Service allows you to post, link, store, share, and otherwise make available certain information, text, graphics, videos, or other material ("Content"). You are responsible for the Content that you post on or through the Service, including its legality, reliability, and appropriateness.</p>
                <p>By posting Content to the Service, you grant us the right and license to use, modify, publicly perform, publicly display, reproduce, and distribute such Content on and through the Service. You retain any and all of your rights to any Content you submit, post, or display on or through the Service and you are responsible for protecting those rights.</p>
                <p>You represent and warrant that: (i) the Content is yours (you own it) or you have the right to use it and grant us the rights and license as provided in these Terms, and (ii) the posting of your Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights, or any other rights of any person.</p>
                
                <h2>7. Intellectual Property</h2>
                <p>The Service and its original content (excluding Content provided by users), features, and functionality are and will remain the exclusive property of Mark Peric and its licensors. The Service is protected by copyright, trademark, and other laws of both Australia and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Mark Peric.</p>
                
                <h2>8. Disclaimers</h2>
                <p>Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.</p>
                <p>Gig Manager does not warrant that: (a) the Service will function uninterrupted, secure, or available at any particular time or location; (b) any errors or defects will be corrected; (c) the Service is free of viruses or other harmful components; or (d) the results of using the Service will meet your requirements.</p>
                
                <h2>9. Limitation of Liability</h2>
                <p>In no event shall Mark Peric, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; (iii) any content obtained from the Service; and (iv) unauthorized access, use, or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence), or any other legal theory, whether or not we have been informed of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its essential purpose.</p>
                
                <h2>10. Indemnification</h2>
                <p>You agree to defend, indemnify, and hold harmless Mark Peric and its licensee and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of a) your use and access of the Service, by you or any person using your account and password; b) a breach of these Terms, or c) Content posted on the Service.</p>
                
                <h2>11. Governing Law</h2>
                <p>These Terms shall be governed and construed in accordance with the laws of Australia, without regard to its conflict of law provisions.</p>
                <p>Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.</p>
                
                <h2>12. Termination</h2>
                <p>We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service.</p>
                
                <h2>13. Severability</h2>
                <p>If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect and enforceable.</p>
                
                <h2>14. Entire Agreement</h2>
                <p>These Terms and our Privacy Policy constitute the entire agreement between us regarding our Service, and supersede and replace any prior agreements we might have between us regarding the Service.</p>
                
                <h2>15. Contact Us</h2>
                <p>If you have any questions about these Terms, please contact us:</p>
                <ul>
                  <li><strong>By email:</strong> <a href="mailto:mark@markperic.com">mark@markperic.com</a></li>
                </ul>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-primary"
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Protected Route Component
const ProtectedApp = () => {
  const { logout, user } = useAuth();

  return (
    <div className="app-container">
      <GigManagerApp onLogout={logout} currentUser={user} />
    </div>
  );
};

// Main Gig Manager App (renamed from previous App component)
const GigForm = ({ onSave, editingGig, onCancel, getColorByGigStatus, onArtistsUpdate, onVenuesUpdate }) => {
  const [formData, setFormData] = useState({
    date: "",
    time: "",
    time_ampm: "PM",
    end_time: "",
    end_time_ampm: "PM",
    performance_name: "",
    performance_fee: "",
    venue: "",
    notes: "",
    band_members: [],
    invoice_status: "not_generated",
    gig_status: "hold"
  });
  
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [saveTimeout, setSaveTimeout] = useState(null);

  useEffect(() => {
    if (editingGig) {
      // Convert 24-hour time to 12-hour format for editing
      const convertTo12Hour = (timeStr) => {
        if (!timeStr) return { time: "", ampm: "PM" };
        const [hours, minutes] = timeStr.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? "PM" : "AM";
        return { time: `${hour12}:${minutes}`, ampm };
      };

      const startTime = convertTo12Hour(editingGig.time);
      const endTime = convertTo12Hour(editingGig.end_time);

      setFormData({
        date: editingGig.date,
        time: startTime.time,
        time_ampm: startTime.ampm,
        end_time: endTime.time,
        end_time_ampm: endTime.ampm,
        performance_name: editingGig.performance_name,
        performance_fee: editingGig.performance_fee,
        venue: editingGig.venue,
        notes: editingGig.notes || "",
        band_members: editingGig.band_members || [],
        invoice_status: editingGig.invoice_status,
        gig_status: editingGig.gig_status || "hold"
      });
    }
  }, [editingGig]);

  // Convert 12-hour time to 24-hour format for API
  const convertTo24Hour = (time12, ampm) => {
    if (!time12) return "";
    const [hours, minutes] = time12.split(':');
    let hour24 = parseInt(hours);
    if (ampm === "AM" && hour24 === 12) hour24 = 0;
    if (ampm === "PM" && hour24 !== 12) hour24 += 12;
    return `${hour24.toString().padStart(2, '0')}:${minutes}`;
  };

  // Auto-save function with debouncing
  const autoSave = async (data) => {
    // Only auto-save if we're editing an existing gig
    if (!editingGig) return;
    
    // Validate required fields
    if (!data.date || !data.time || !data.performance_name || !data.venue) {
      return;
    }

    setSaveStatus('saving');
    
    try {
      const gigData = {
        ...data,
        time: convertTo24Hour(data.time, data.time_ampm),
        end_time: data.end_time ? convertTo24Hour(data.end_time, data.end_time_ampm) : "",
        performance_fee: parseFloat(data.performance_fee) || 0,
        color: getColorByGigStatus(data.gig_status)
      };

      // Remove the ampm fields before sending to API
      const { time_ampm, end_time_ampm, ...apiData } = gigData;

      await axios.put(`${API}/gigs/${editingGig.id}`, apiData);
      setSaveStatus('saved');
      
      // Reset to idle after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
      
    } catch (error) {
      console.error("Auto-save error:", error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Debounced auto-save effect
  useEffect(() => {
    if (!editingGig) return; // Don't auto-save for new gigs
    
    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Set new timeout for auto-save (1.5 seconds after user stops typing)
    const newTimeout = setTimeout(() => {
      autoSave(formData);
    }, 1500);
    
    setSaveTimeout(newTimeout);
    
    // Cleanup timeout on unmount
    return () => {
      if (newTimeout) {
        clearTimeout(newTimeout);
      }
    };
  }, [formData]); // Trigger when form data changes

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For new gigs, use manual save
    if (!editingGig) {
      console.log("Creating new gig with data:", formData);
      
      try {
        const gigData = {
          ...formData,
          time: convertTo24Hour(formData.time, formData.time_ampm),
          end_time: formData.end_time ? convertTo24Hour(formData.end_time, formData.end_time_ampm) : "",
          performance_fee: parseFloat(formData.performance_fee),
          color: getColorByGigStatus(formData.gig_status)
        };

        // Remove the ampm fields before sending to API
        const { time_ampm, end_time_ampm, ...apiData } = gigData;
        
        console.log("Sending new gig data:", apiData);
        const response = await axios.post(`${API}/gigs`, apiData);
        console.log("Create response:", response.data);
        
        onSave();
        setFormData({
          date: "",
          time: "",
          time_ampm: "PM",
          end_time: "",
          end_time_ampm: "PM",
          performance_name: "",
          performance_fee: "",
          venue: "",
          notes: "",
          invoice_status: "not_generated",
          gig_status: "hold"
        });
      } catch (error) {
        console.error("Error creating gig:", error);
        alert("Error creating gig: " + (error.response?.data?.detail || error.message));
      }
      return;
    }
    
    // For editing, trigger immediate save (since auto-save handles most changes)
    await autoSave(formData);
    onSave(); // Close the form
  };

  // Generate 12-hour time options (every 30 minutes)
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 1; hour <= 12; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 30) {
        const timeString = `${hour}:${minutes.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    return options;
  };

  return (
    <form onSubmit={handleSubmit} className="gig-form">
      <div className="form-grid-new">
        {/* Row 1: Date and Performance Name */}
        <div className="form-field">
          <label className="form-label-inline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            Date
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({...formData, date: e.target.value})}
            required
          />
        </div>
        
        <div className="form-field">
          <AutocompleteInput
            value={formData.performance_name}
            onChange={(value) => setFormData({...formData, performance_name: value})}
            onSelect={(value) => setFormData({...formData, performance_name: value})}
            onItemCreated={() => onArtistsUpdate && onArtistsUpdate()}
            placeholder="Enter artist name"
            searchEndpoint="/artists/search"
            createEndpoint="/artists"
            label="Artist"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
              </svg>
            }
            className="form-input"
          />
        </div>

        {/* Row 2: Start Time and End Time */}
        <div className="form-field">
          <label className="form-label-inline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12,6 12,12 16,14"></polyline>
            </svg>
            Start Time (Optional)
          </label>
          <div className="time-input-group">
            <select
              className="time-select"
              value={formData.time}
              onChange={(e) => setFormData({...formData, time: e.target.value})}
            >
              <option value="">Select time</option>
              {generateTimeOptions().map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
            <div className="ampm-toggle">
              <button
                type="button"
                className={`ampm-btn ${formData.time_ampm === 'AM' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, time_ampm: 'AM'})}
              >
                AM
              </button>
              <button
                type="button"
                className={`ampm-btn ${formData.time_ampm === 'PM' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, time_ampm: 'PM'})}
              >
                PM
              </button>
            </div>
          </div>
        </div>
        
        <div className="form-field">
          <label className="form-label-inline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12,6 12,12 16,14"></polyline>
            </svg>
            End Time
          </label>
          <div className="time-input-group">
            <select
              className="time-select"
              value={formData.end_time}
              onChange={(e) => setFormData({...formData, end_time: e.target.value})}
            >
              <option value="">Optional</option>
              {generateTimeOptions().map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
            <div className="ampm-toggle">
              <button
                type="button"
                className={`ampm-btn ${formData.end_time_ampm === 'AM' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, end_time_ampm: 'AM'})}
              >
                AM
              </button>
              <button
                type="button"
                className={`ampm-btn ${formData.end_time_ampm === 'PM' ? 'active' : ''}`}
                onClick={() => setFormData({...formData, end_time_ampm: 'PM'})}
              >
                PM
              </button>
            </div>
          </div>
        </div>

        {/* Row 3: Venue and Performance Fee */}
        <div className="form-field">
          <AutocompleteInput
            value={formData.venue}
            onChange={(value) => setFormData({...formData, venue: value})}
            onSelect={(value) => setFormData({...formData, venue: value})}
            onItemCreated={() => onVenuesUpdate && onVenuesUpdate()}
            placeholder="e.g., The Blue Note"
            searchEndpoint="/venues/search"
            createEndpoint="/venues"
            label="Venue"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            }
            className="form-input"
          />
        </div>
        
        <div className="form-field">
          <label className="form-label-inline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            Performance Fee ($)
          </label>
          <input
            type="number"
            step="0.01"
            placeholder="0"
            value={formData.performance_fee}
            onChange={(e) => setFormData({...formData, performance_fee: e.target.value})}
            required
          />
        </div>

        {/* Row 4: Gig Status and Invoice Status */}
        <div className="form-field">
          <label className="form-label-inline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
            Gig Status
          </label>
          <select
            value={formData.gig_status}
            onChange={(e) => setFormData({...formData, gig_status: e.target.value})}
          >
            <option value="hold">Hold</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        
        <div className="form-field">
          <label className="form-label-inline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
            Invoice Status
          </label>
          <select
            value={formData.invoice_status}
            onChange={(e) => setFormData({...formData, invoice_status: e.target.value})}
          >
            <option value="not_generated">Not Generated</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      {/* Notes field - full width at bottom */}
      <div className="form-field-full">
        <label className="form-label-top">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="form-icon-inline">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
          </svg>
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          rows="3"
          placeholder="Additional notes about the gig..."
        />
      </div>

      {/* Band Members field */}
      <div className="form-field-full">
        <BandMemberDisplay 
          gigId={editingGig?.id}
          bandMembers={formData.band_members || []}
          onUpdate={() => {
            // Refresh the page to show updated band members
            window.location.reload();
          }}
        />
      </div>
      
      {/* Auto-save status indicator */}
      {editingGig && (
        <div className="auto-save-status">
          {saveStatus === 'saving' && (
            <div className="save-indicator saving">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.35 0 4.48.9 6.07 2.38"/>
              </svg>
              Saving...
            </div>
          )}
          {saveStatus === 'saved' && (
            <div className="save-indicator saved">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
              Saved
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="save-indicator error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              Save failed
            </div>
          )}
        </div>
      )}
      
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          {editingGig ? "Done" : "Create Gig"}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          Cancel
        </button>
      </div>
    </form>
  );
};

const CalendarView = ({ gigs, onEditGig, onAddGig, onUpdateGig, onShowInviteModal }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedGig, setSelectedGig] = useState(null);
  
  // Get today's date for highlighting
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  // Helper function to check if a date is today
  const isToday = (date) => {
    if (!date) return false;
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return dateStr === todayStr;
  };
  
  // Helper function to check if a date is in the current month
  const isCurrentMonth = (date) => {
    if (!date) return false;
    return date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear();
  };
  
  // Helper function to format time from 24-hour to 12-hour with AM/PM (no leading zeros)
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add days from the first week of the next month to complete the grid
    // and show the first week of the next month
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      // Fill remaining days in the last row
      for (let day = 1; day <= remainingDays; day++) {
        days.push(new Date(year, month + 1, day));
      }
    }
    
    // Add one more complete week (first week of next month)
    const lastAddedDay = remainingDays < 7 ? remainingDays : 0;
    for (let day = lastAddedDay + 1; day <= lastAddedDay + 7; day++) {
      days.push(new Date(year, month + 1, day));
    }
    
    return days;
  };

  const getGigsForDate = (date) => {
    if (!date) return [];
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return gigs
      .filter(gig => gig.date === dateStr)
      .sort((a, b) => {
        // Sort by time - earliest first
        const timeA = a.time || "00:00";
        const timeB = b.time || "00:00";
        return timeA.localeCompare(timeB);
      });
  };

  const handleGigClick = (gig) => {
    setSelectedGig(gig);
  };

  const closeModal = () => {
    setSelectedGig(null);
  };

  const handleEdit = () => {
    onEditGig(selectedGig);
    setSelectedGig(null);
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>
        <h2>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h2>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>
      </div>
      <div className="calendar-grid">
        <div className="calendar-days-header">
          {[
            { full: "Sun", short: "S" },
            { full: "Mon", short: "M" },
            { full: "Tue", short: "T" },
            { full: "Wed", short: "W" },
            { full: "Thu", short: "T" },
            { full: "Fri", short: "F" },
            { full: "Sat", short: "S" }
          ].map((day, index) => (
            <div key={index} className="calendar-day-header">
              <span className="day-full">{day.full}</span>
              <span className="day-short">{day.short}</span>
            </div>
          ))}
        </div>
        <div className="calendar-days">
          {getDaysInMonth(currentMonth).map((date, index) => (
            <div key={index} className={`calendar-day ${date ? 'active' : 'inactive'} ${date && !isCurrentMonth(date) ? 'next-month' : ''} ${isToday(date) ? 'today' : ''}`}>
              {date && (
                <>
                  <div className="calendar-day-header">
                    <div className={`day-number ${isToday(date) ? 'today-circle' : ''}`}>{date.getDate()}</div>
                    <button 
                      className="calendar-add-btn"
                      onClick={() => onAddGig && onAddGig(date)}
                      title={`Add gig on ${date.toLocaleDateString()}`}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                  </div>
                  <div className="calendar-gigs">
                    {getGigsForDate(date).map(gig => (
                      <div 
                        key={gig.id} 
                        className="calendar-gig clickable"
                        style={{ backgroundColor: gig.color || '#667eea' }}
                        onClick={() => handleGigClick(gig)}
                      >
                        <div className="gig-time">{formatTime(gig.time)}</div>
                        <div className="gig-name">{gig.performance_name}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Gig Detail Modal */}
      {selectedGig && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content gig-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gig-detail-header" style={{ borderColor: selectedGig.color || '#667eea' }}>
              <div className="gig-detail-date">
                {new Date(selectedGig.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="gig-detail-time">{formatTime(selectedGig.time)}</div>
              <div 
                className="gig-color-indicator"
                style={{ backgroundColor: selectedGig.color || '#667eea' }}
              />
            </div>
            <div className="gig-detail-body">
              <h3 className="gig-detail-title">{selectedGig.performance_name}</h3>
              <div className="gig-detail-info">
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span className="gig-detail-label">Venue:</span>
                  <span>{selectedGig.venue}</span>
                </div>
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <span className="gig-detail-label">Fee:</span>
                  <span>${selectedGig.performance_fee}</span>
                </div>
                
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span className="gig-detail-label">Gig Status:</span>
                  <select
                    value={selectedGig.gig_status || "hold"}
                    onChange={(e) => {
                      const updatedGig = { ...selectedGig, gig_status: e.target.value };
                      setSelectedGig(updatedGig);
                      onUpdateGig(selectedGig.id, { gig_status: e.target.value });
                    }}
                    className="status-select inline-status-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="hold">Hold</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10,9 9,9 8,9"></polyline>
                  </svg>
                  <span className="gig-detail-label">Invoice Status:</span>
                  <select
                    value={selectedGig.invoice_status || "not_generated"}
                    onChange={(e) => {
                      const updatedGig = { ...selectedGig, invoice_status: e.target.value };
                      setSelectedGig(updatedGig);
                      onUpdateGig(selectedGig.id, { invoice_status: e.target.value });
                    }}
                    className="status-select inline-status-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="not_generated">Not Generated</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              {selectedGig.notes && (
                <div className="gig-detail-notes">
                  <div className="notes-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                    </svg>
                    <span className="gig-detail-label">Notes:</span>
                  </div>
                  <p>{selectedGig.notes}</p>
                </div>
              )}
              
              {/* Collaborators Section */}
              {selectedGig.collaborators && selectedGig.collaborators.length > 0 && (
                <div className="gig-collaborators-section">
                  <div className="collaborators-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <circle cx="16" cy="7" r="3"></circle>
                    </svg>
                    <span className="gig-detail-label">Band Members:</span>
                  </div>
                  <div className="collaborators-list">
                    {selectedGig.collaborators.map((collaborator, index) => (
                      <div key={collaborator.id} className="collaborator-item">
                        <div className="collaborator-avatar">
                          {collaborator.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="collaborator-info">
                          <span className="collaborator-name">{collaborator.username}</span>
                          <span className={`collaborator-role ${collaborator.role}`}>
                            {collaborator.role === 'owner' ? 'Owner' : 'Collaborator'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <BandMemberDisplay 
                gigId={selectedGig.id}
                bandMembers={selectedGig.band_members || []}
                onUpdate={() => {
                  // Just close the selector - no need to reload the entire page
                  console.log('Band members updated successfully');
                }}
              />
              
              {/* Invite Musicians Section */}
              {(!selectedGig.is_shared || selectedGig.user_permission === 'owner') && (
                <div className="invite-musicians-section">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onShowInviteModal(selectedGig);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onShowInviteModal(selectedGig);
                    }}
                    className="btn btn-outline invite-btn mobile-touch-btn"
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                    Invite Musicians
                  </button>
                </div>
              )}
              
              {/* Shared Gig Indicator */}
              {selectedGig.is_shared && (
                <div className="shared-gig-indicator">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  <span>Shared Gig - View Only</span>
                </div>
              )}
            </div>
            
            <div className="gig-detail-actions">
              <button onClick={handleEdit} className="btn btn-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit Gig
              </button>
              <button onClick={closeModal} className="btn btn-secondary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KanbanView = ({ gigs, onUpdateGig, onGigClick }) => {
  const sortedGigs = [...gigs].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Helper function to format time from 24-hour to 12-hour with AM/PM (no leading zeros)
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="kanban-view">
      <div className="kanban-timeline">
        {sortedGigs.map(gig => (
          <div 
            key={gig.id} 
            className="kanban-card clickable"
            style={{ 
              borderColor: gig.color || '#667eea',
              backgroundColor: `${gig.color || '#667eea'}10`,
              cursor: 'pointer'
            }}
            onClick={() => onGigClick(gig)}
          >
            <div className="kanban-card-header">
              <div className="kanban-date-time">
                <div className="kanban-date">{formatDate(gig.date)}</div>
                <div className="kanban-time">{formatTime(gig.time)}{gig.end_time && ` - ${formatTime(gig.end_time)}`}</div>
              </div>
              <div 
                className="kanban-color-indicator"
                style={{ backgroundColor: gig.color || '#667eea' }}
              />
            </div>
            
            <div className="kanban-card-body">
              <h3 className="kanban-title">{gig.performance_name}</h3>
              
              <div className="kanban-detail-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="kanban-icon">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span className="kanban-label">Venue:</span>
                <span className="kanban-value">{gig.venue}</span>
              </div>
              
              <div className="kanban-detail-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="kanban-icon">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <span className="kanban-label">Fee:</span>
                <span className="kanban-value">${gig.performance_fee}</span>
              </div>
              
              <div className="kanban-detail-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="kanban-icon">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
                <span className="kanban-label">Gig Status:</span>
                <select
                  value={gig.gig_status || "hold"}
                  onChange={(e) => onUpdateGig(gig.id, { gig_status: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  className="status-select inline-status-select kanban-select"
                >
                  <option value="hold">Hold</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              
              <div className="kanban-detail-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="kanban-icon">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
                <span className="kanban-label">Invoice Status:</span>
                <select
                  value={gig.invoice_status || "not_generated"}
                  onChange={(e) => onUpdateGig(gig.id, { invoice_status: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  className="status-select inline-status-select kanban-select"
                >
                  <option value="not_generated">Not Generated</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              
              {gig.notes && (
                <div className="kanban-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="kanban-icon">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                  </svg>
                  <span className="kanban-label">Notes:</span>
                  <span className="kanban-value">{gig.notes}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SpreadsheetView = ({ gigs, onEditGig, onArchiveGig, onDeleteGig, onGigClick, onUpdateGig }) => {
  
  // Helper function to format time from 24-hour to 12-hour with AM/PM (no leading zeros)
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to format date as "Oct 7th 2025"
  const formatReadableDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day >= 11 && day <= 13) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';  
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${month} ${day}${getOrdinalSuffix(day)} ${year}`;
  };

  return (
    <div className="spreadsheet-view">
      <div className="table-wrapper">
        <table className="gigs-table">
          <thead>
            <tr>
              <th>Colour</th>
              <th>Date</th>
              <th>Performance</th>
              <th>Fee</th>
              <th>Venue</th>
              <th>Time</th>
              <th>Invoice Status</th>
              <th>Gig Status</th>
              <th>Actions</th>
            </tr>
          </thead>
        <tbody>
          {gigs.map(gig => (
            <tr 
              key={gig.id} 
              className="clickable-row"
              onClick={() => onGigClick(gig)}
            >
              <td>
                <div 
                  className="color-indicator"
                  style={{ backgroundColor: gig.color || '#37323E' }}
                />
              </td>
              <td>{formatReadableDate(gig.date)}</td>
              <td>{gig.performance_name}</td>
              <td>${gig.performance_fee}</td>
              <td>{gig.venue}</td>
              <td>{formatTime(gig.time)}{gig.end_time && ` - ${formatTime(gig.end_time)}`}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={gig.invoice_status}
                  onChange={(e) => onUpdateGig && onUpdateGig(gig.id, { invoice_status: e.target.value })}
                  className="status-select inline-status-select"
                >
                  <option value="not_generated">Not Generated</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  value={gig.gig_status || "hold"}
                  onChange={(e) => onUpdateGig && onUpdateGig(gig.id, { gig_status: e.target.value })}
                  className="status-select inline-status-select"
                >
                  <option value="hold">Hold</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <div className="actions-inline">
                  <button 
                    onClick={() => onEditGig(gig)} 
                    className="btn-icon-action btn-edit"
                    title="Edit Gig"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path d="M12 20h9"></path>
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                    </svg>
                  </button>
                  <button 
                    onClick={() => onArchiveGig(gig.id)} 
                    className="btn-icon-action btn-archive"
                    title="Archive Gig"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <polyline points="21,8 21,21 3,21 3,8"></polyline>
                      <rect x="1" y="3" width="22" height="5"></rect>
                      <line x1="10" y1="12" x2="14" y2="12"></line>
                    </svg>
                  </button>
                  <button 
                    onClick={() => onDeleteGig(gig.id)} 
                    className="btn-icon-action btn-delete"
                    title="Delete Gig"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const ReportsView = ({ reports, summary }) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState('');

  const API = `${process.env.REACT_APP_BACKEND_URL || 'https://gigloader.preview.emergentagent.com'}/api`;

  // Function to convert gig data to CSV format
  const convertToCSV = (gigs) => {
    if (!gigs || gigs.length === 0) {
      return 'No data available';
    }

    // Define CSV headers
    const headers = [
      'Date',
      'Performance Name',
      'Venue', 
      'Start Time',
      'End Time',
      'Performance Fee',
      'Invoice Status',
      'Gig Status',
      'Notes',
      'Color',
      'Archived',
      'Created At',
      'Updated At'
    ];

    // Convert gigs to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...gigs.map(gig => {
        // Format and escape values for CSV
        const formatValue = (value) => {
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };

        // Format time from 24-hour to 12-hour with AM/PM
        const formatTime = (timeStr) => {
          if (!timeStr) return '';
          const [hours, minutes] = timeStr.split(':');
          const hour24 = parseInt(hours);
          const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
          const ampm = hour24 >= 12 ? 'PM' : 'AM';
          return `${hour12}:${minutes} ${ampm}`;
        };

        // Format date for better readability
        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          });
        };

        return [
          formatValue(formatDate(gig.date)),
          formatValue(gig.performance_name || ''),
          formatValue(gig.venue || ''),
          formatValue(formatTime(gig.start_time)),
          formatValue(formatTime(gig.end_time)),
          formatValue(gig.performance_fee ? `$${gig.performance_fee}` : ''),
          formatValue(gig.invoice_status || ''),
          formatValue(gig.gig_status || ''),
          formatValue(gig.notes || ''),
          formatValue(gig.color || ''),
          formatValue(gig.archived ? 'Yes' : 'No'),
          formatValue(formatDate(gig.created_at)),
          formatValue(formatDate(gig.updated_at))
        ].join(',');
      })
    ];

    return csvRows.join('\n');
  };

  // Simple, reliable CSV download function
  const downloadCSVFile = (csvContent, filename) => {
    // Create blob with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { 
      type: 'text/csv;charset=utf-8;' 
    });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Set download attributes
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Hidden iframe download method - most reliable
  const exportGigsToCSV = async () => {
    setExportLoading(true);
    setExportStatus('Preparing CSV download...');

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setExportStatus('Please log in first');
        setExportLoading(false);
        return;
      }

      // Use direct browser navigation for reliable download
      // The backend CSV endpoint expects token as URL parameter
      const exportUrl = `${API}/export/csv?token=${encodeURIComponent(token)}`;
      console.log('CSV Export URL:', exportUrl);
      
      // Method 1: Try using window.open() first (most reliable)
      const newWindow = window.open(exportUrl, '_blank');
      
      if (newWindow) {
        // Window opened successfully
        setExportStatus('CSV download started - check your downloads folder!');
        
        // Close the window after a short delay (download should have started)
        setTimeout(() => {
          if (newWindow && !newWindow.closed) {
            newWindow.close();
          }
        }, 3000);
      } else {
        // Pop-up blocked, fallback to iframe method
        console.log('Window.open blocked, trying iframe method...');
        
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.src = exportUrl;
        
        document.body.appendChild(iframe);
        
        // Remove iframe after download should have started
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 5000);
        
        setExportStatus('CSV download started via iframe - check your downloads folder!');
      }
      
    } catch (error) {
      console.error('CSV Export Error:', error);
      setExportStatus(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
      setTimeout(() => setExportStatus(''), 8000);
    }
  };

  return (
    <div className="reports-view">
      {/* Export Section */}
      <div className="export-section" style={{
        marginBottom: '2rem',
        padding: '1rem',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#f8fafc'
      }}>
        <h3 style={{ 
          fontSize: '1.1rem', 
          fontWeight: '600', 
          margin: '0 0 1rem 0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          color: '#374151'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10,9 9,9 8,9"></polyline>
          </svg>
          Export Data
        </h3>
        
        <button 
          onClick={exportGigsToCSV}
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontWeight: '600',
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
        >
          Export to CSV
        </button>
        {exportStatus && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem',
            borderRadius: '4px',
            fontSize: '0.85rem',
            backgroundColor: exportStatus.includes('started') || includes('completed') ? '#dcfce7' : '#fef2f2',
            color: exportStatus.includes('started') || includes('completed') ? '#16a34a' : '#dc2626',
            border: `1px solid ${exportStatus.includes('started') || includes('completed') ? '#bbf7d0' : '#fecaca'}`
          }}>
            {exportStatus}
          </div>
        )}
      </div>

      <div className="summary-cards">
        <div className="stats-card stats-card-total">
          <h3>Total Gigs</h3>
          <p className="stat-number">{summary.total_gigs}</p>
        </div>
        <div className="stats-card stats-card-income">
          <h3>Total Income</h3>
          <p className="stat-number">${summary.total_income}</p>
        </div>
        <div className="stats-card stats-card-paid">
          <h3>Paid Gigs</h3>
          <p className="stat-number">{summary.paid_gigs}</p>
        </div>
        <div className="stats-card stats-card-pending">
          <h3>Pending</h3>
          <p className="stat-number">{summary.pending_gigs}</p>
        </div>
      </div>
      
      <div className="monthly-reports">
        <h3>Monthly Reports</h3>
        <table className="reports-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Total Gigs</th>
              <th>Total Income</th>
              <th>Paid Gigs</th>
              <th>Average Fee</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report, index) => (
              <tr key={index}>
                <td>{report.month} {report.year}</td>
                <td>{report.total_gigs}</td>
                <td>${report.total_income}</td>
                <td>{report.paid_gigs}</td>
                <td>${report.average_fee}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


    </div>
  );
};

const ArchiveView = ({ archivedGigs, onRestoreGig, onDeleteGig }) => {
  // Helper function to format time from 24-hour to 12-hour with AM/PM (no leading zeros)
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to format date as "Oct 7th 2025"
  const formatDateForTable = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day >= 11 && day <= 13) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';  
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${month} ${day}${getOrdinalSuffix(day)} ${year}`;
  };

  // Function to get color based on gig status
  const getColorByGigStatus = (gigStatus) => {
    switch (gigStatus) {
      case 'hold': return '#BB0A21';
      case 'confirmed': return '#3FA34D';
      case 'completed': return '#BFB6BB';
      default: return '#BB0A21'; // Default to hold color
    }
  };

  return (
    <div className="archive-view">
      <h2>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="section-icon">
          <path d="M21 8v13H3V8"></path>
          <path d="M1 3h22v5H1z"></path>
          <path d="M10 12h4"></path>
        </svg>
        Archived Gigs
      </h2>
      
      {archivedGigs.length === 0 ? (
        <div className="empty-state">
          <p>No archived gigs yet.</p>
        </div>
      ) : (
        <div className="gigs-table-container">
          <table className="gigs-table">
            <thead>
              <tr>
                <th style={{ backgroundColor: '#f8f9fa' }}></th>
                <th>Date</th>
                <th>Performance</th>
                <th>Fee</th>
                <th>Venue</th>
                <th>Time</th>
                <th>Gig Status</th>
                <th>Invoice Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {archivedGigs.map(gig => (
                <tr key={gig.id}>
                  <td>
                    <div 
                      className="color-indicator" 
                      style={{ backgroundColor: getColorByGigStatus(gig.gig_status) }}
                    ></div>
                  </td>
                  <td>{formatDateForTable(gig.date)}</td>
                  <td>{gig.performance_name}</td>
                  <td>${gig.performance_fee}</td>
                  <td>{gig.venue}</td>
                  <td>{formatTime(gig.time)}{gig.end_time && ` - ${formatTime(gig.end_time)}`}</td>
                  <td>
                    {gig.collaborators && gig.collaborators.length > 0 ? (
                      <div className="gig-collaborators-inline">
                        {gig.collaborators.slice(0, 3).map((collaborator, index) => (
                          <span key={collaborator.id} className={`collaborator-badge ${collaborator.role}`}>
                            {collaborator.role === 'owner' ? 'Owner: ' : ''} {collaborator.username}
                          </span>
                        ))}
                        {gig.collaborators.length > 3 && (
                          <span className="collaborator-badge">
                            +{gig.collaborators.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="no-collaborators">Solo</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${gig.gig_status.toLowerCase().replace(' ', '-')}`}>
                      {gig.gig_status}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${gig.invoice_status.toLowerCase().replace(' ', '-')}`}>
                      {gig.invoice_status}
                    </span>
                  </td>
                  <td>
                    <div className="actions-inline">
                      <button onClick={() => onRestoreGig(gig.id)} className="btn-icon-action" title="Restore">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="23,18 23,23 18,23"></polyline>
                          <path d="M21,12.3A9,9 0 1,1 8.7,2.7l0.8,0.8A8,8 0 1,0 20.3,12.3"></path>
                        </svg>
                      </button>
                      <button onClick={() => onDeleteGig(gig.id)} className="btn-icon-action" title="Delete Permanently">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6V20A2,2 0 0,1 17,20H7A2,2 0 0,1 5,20V6M8,6V4A2,2 0 0,1 10,4H14A2,2 0 0,1 16,4V6"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Settings View Component
const SettingsView = ({ 
  userProfile, 
  artists, 
  venues, 
  reports,
  summary,
  onUserProfileUpdate, 
  onArtistsUpdate, 
  onVenuesUpdate, 
  currentUser 
}) => {
  const { user } = useAuth(); // Get current user
  const [activeTab, setActiveTab] = useState('personal');
  const [allUsers, setAllUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  
  // Backup states - moved to top level to fix React Hooks error
  const [backupStatus, setBackupStatus] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

  const fetchAllUsers = async () => {
    // Only fetch if user is admin
    if (!user || !['admin', 'markperic'].includes(user.username)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Check if current user is admin
  const isAdmin = user && ['admin', 'markperic'].includes(user.username);



  useEffect(() => {
    if (isAdmin) {
      fetchAllUsers();
    }
  }, [isAdmin]);

  const renderPersonalDetails = () => (
    <PersonalDetailsTab 
      profile={userProfile} 
      onProfileUpdate={onUserProfileUpdate}
    />
  );

  const renderArtists = () => (
    <ArtistsTab 
      artists={artists} 
      onArtistsUpdate={onArtistsUpdate}
    />
  );

  const renderVenues = () => (
    <VenuesTab 
      venues={venues} 
      onVenuesUpdate={onVenuesUpdate}
    />
  );

  const renderUsers = () => (
    <UsersTab 
      users={allUsers} 
      onUsersUpdate={fetchAllUsers}
      currentUser={user}
    />
  );



  const renderBackup = () => {
    // Universal download function for backup section
    const forceDownload = (content, filename, contentType = 'text/plain') => {
      try {
        // Create blob with BOM for better Excel compatibility if it's CSV
        const BOM = contentType.includes('csv') ? '\uFEFF' : '';
        const blob = new Blob([BOM + content], { type: contentType + ';charset=utf-8;' });
        
        // Method 1: IE/Edge
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, filename);
          return true;
        }
        
        // Method 2: Modern browsers with download attribute
        if ('download' in document.createElement('a')) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          
          link.href = url;
          link.download = filename;
          link.type = contentType;
          
          // Ensure link is not visible
          link.style.position = 'absolute';
          link.style.left = '-9999px';
          link.style.top = '-9999px';
          link.style.visibility = 'hidden';
          link.style.opacity = '0';
          
          // Add to DOM and trigger download
          document.body.appendChild(link);
          
          // Use multiple trigger methods for maximum compatibility
          try {
            link.click();
          } catch (e) {
            // Fallback click method
            const evt = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            link.dispatchEvent(evt);
          }
          
          // Cleanup after a short delay
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
          }, 150);
          
          return true;
        }
        
        // Method 3: Data URI fallback for older browsers
        const maxSize = 2 * 1024 * 1024; // 2MB limit for data URIs
        if (content.length < maxSize) {
          const encodedContent = encodeURIComponent(BOM + content);
          const dataUri = `data:${contentType};charset=utf-8,${encodedContent}`;
          
          // Try to open in new window for download
          const popup = window.open(dataUri, '_blank', 'width=1,height=1');
          if (popup) {
            setTimeout(() => popup.close(), 1000);
            return true;
          }
        }
        
        // Method 4: Last resort - show in new window for manual save
        throw new Error('All download methods failed');
        
      } catch (error) {
        console.warn('Download failed, showing content for manual save:', error);
        
        // Ultimate fallback - new window with instructions
        const popup = window.open('', '_blank', 'width=800,height=600');
        if (popup) {
          popup.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Download Failed - Manual Save Required</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                textarea { width: 100%; height: 400px; font-family: monospace; font-size: 12px; }
                .instructions { background: #f0f8ff; padding: 15px; border-radius: 5px; margin: 15px 0; }
                .button { background: #007cba; color: white; padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; }
              </style>
            </head>
            <body>
              <h2>Manual Download Required</h2>
              <div class="instructions">
                <strong>To save your data:</strong><br>
                1. Select all text in the box below (Ctrl+A or Cmd+A)<br>
                2. Copy it (Ctrl+C or Cmd+C)<br>
                3. Open a text editor (Notepad, TextEdit, etc.)<br>
                4. Paste the content and save as "${filename}"
              </div>
              <button class="button" onclick="document.querySelector('textarea').select(); document.execCommand('copy'); alert('Content copied to clipboard!');">
                Select All & Copy
              </button>
              <button class="button" onclick="window.close();" style="margin-left: 10px;">Close</button>
              <br><br>
              <textarea readonly onclick="this.select();">${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
            </body>
            </html>
          `);
          popup.focus();
        }
        return false;
      }
    };

    const downloadBackup = () => {
      setIsExporting(true);
      setBackupStatus('Preparing JSON backup download...');
      
      try {
        const token = localStorage.getItem('token');
        
        console.log('JSON Backup Debug:');
        console.log('- Token exists:', !!token);
        console.log('- API URL:', API);
        
        if (!token) {
          console.log('- ERROR: No token found');
          setBackupStatus('Please log in first');
          setIsExporting(false);
          return;
        }

        // Use direct browser navigation for reliable download
        // The backend backup endpoint expects token as URL parameter
        const exportUrl = `${API}/backup/export?token=${encodeURIComponent(token)}`;
        console.log('- Backup URL:', exportUrl);
        
        // Method 1: Try using window.open() first (most reliable)
        const newWindow = window.open(exportUrl, '_blank');
        
        if (newWindow) {
          // Window opened successfully
          setBackupStatus('JSON backup download started - check your downloads folder!');
          
          // Close the window after a short delay (download should have started)
          setTimeout(() => {
            if (newWindow && !newWindow.closed) {
              newWindow.close();
            }
          }, 3000);
        } else {
          // Pop-up blocked, fallback to iframe method
          console.log('- Window.open blocked, trying iframe method...');
          
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.top = '-9999px';
          iframe.src = exportUrl;
          
          // Add iframe to page
          document.body.appendChild(iframe);
          
          // Remove iframe after download should have started
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
              console.log('- Backup iframe removed');
            }
          }, 5000);
          
          setBackupStatus('JSON backup download started via iframe - check your downloads folder!');
        }
        
        console.log('- Download initiation method completed');
        
      } catch (error) {
        console.error('Backup Export Error:', error);
        setBackupStatus(`Export failed: ${error.message}`);
      } finally {
        setIsExporting(false);
        setTimeout(() => setBackupStatus(''), 8000);
      }
    };

    const importBackup = async () => {
      if (!importFile) {
        setBackupStatus('Please select a backup file first');
        return;
      }

      setIsImporting(true);
      setBackupStatus('');
      
      try {
        const fileContent = await importFile.text();
        const backupData = JSON.parse(fileContent);
        
        // Validate backup data structure
        if (!backupData.gigs || !Array.isArray(backupData.gigs)) {
          throw new Error('Invalid backup file format - missing gigs array');
        }

        const response = await axios.post(`${API}/backup/import`, {
          gigs: backupData.gigs,
          overwrite_existing: overwriteExisting
        });

        const result = response.data;
        setBackupStatus(
          `Import completed! Imported: ${result.imported_count}, Updated: ${result.updated_count}, Errors: ${result.error_count}`
        );
        
        if (result.errors && result.errors.length > 0) {
          setBackupStatus(prev => prev + '\nSome errors occurred:\n' + result.errors.join('\n'));
        }
        
        // Clear the file input
        setImportFile(null);
        
      } catch (error) {
        console.error('Backup import error:', error);
        let errorMessage = 'Import failed';
        if (error.message.includes('JSON')) {
          errorMessage = 'Invalid JSON file format';
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        setBackupStatus(`${errorMessage}`);
      } finally {
        setIsImporting(false);
      }
    };

    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
          setBackupStatus('Please select a JSON file');
          return;
        }
        setImportFile(file);
        setBackupStatus(`Selected: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      }
    };

    return (
      <div className="backup-tab">
        <div className="backup-section">
          <h3 className="backup-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7,10 12,15 17,10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export Backup
          </h3>
          <p className="backup-description">
            Download a complete backup of your gigs as a JSON file. This includes all gig details, 
            dates, venues, notes, and settings.
          </p>
          
          <button 
            onClick={downloadBackup}
            style={{
              display: 'inline-block',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Export Backup
          </button>
        </div>

        <div className="backup-section">
          <h3 className="backup-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17,8 12,3 7,8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Import Backup
          </h3>
          <p className="backup-description">
            Restore your gigs from a previously exported JSON backup file. 
            You can choose to update existing gigs or skip duplicates.
          </p>
          
          <div className="backup-import-controls">
            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="file-input"
                id="backup-file-input"
                disabled={isImporting}
              />
              <label htmlFor="backup-file-input" className="file-input-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                </svg>
                {importFile ? 'Change File' : 'Choose Backup File'}
              </label>
            </div>

            <div className="backup-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={overwriteExisting}
                  onChange={(e) => setOverwriteExisting(e.target.checked)}
                  disabled={isImporting}
                />
                <span className="checkbox-custom"></span>
                Overwrite existing gigs with same name, date, and venue
              </label>
            </div>

            <button 
              onClick={importBackup}
              disabled={isImporting || !importFile}
              className="backup-button backup-import"
            >
              {isImporting ? (
                <>
                  <div className="spinner"></div>
                  Importing...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17,8 12,3 7,8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  Import Backup
                </>
              )}
            </button>
          </div>
        </div>

        {backupStatus && (
          <div className={`backup-status ${backupStatus.includes('started') || includes('completed') ? 'success' : backupStatus.includes('failed') || includes('Error') ? 'error' : 'info'}`}>
            <pre>{backupStatus}</pre>
          </div>
        )}

        <div className="backup-info">
          <h4>💡 Backup Tips</h4>
          <ul>
            <li>Regular backups help prevent data loss during deployments or updates</li>
            <li>Backup files contain all your gig data in JSON format</li>
            <li>You can edit backup files manually before importing if needed</li>
            <li>Import will skip existing gigs unless "Overwrite" is checked</li>
            <li>Keep backup files in a safe location like cloud storage</li>
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="settings-view">
      <div className="settings-tabs">
        <button 
          className={`settings-tab ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          Personal Details
        </button>
        <button 
          className={`settings-tab ${activeTab === 'artists' ? 'active' : ''}`}
          onClick={() => setActiveTab('artists')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
          </svg>
          Artists
        </button>
        <button 
          className={`settings-tab ${activeTab === 'venues' ? 'active' : ''}`}
          onClick={() => setActiveTab('venues')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          Venues
        </button>

        {isAdmin && (
          <button 
            className={`settings-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <circle cx="16" cy="7" r="3"></circle>
            </svg>
            Users
          </button>
        )}

        <button 
          className={`settings-tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10"></line>
            <line x1="12" y1="20" x2="12" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="14"></line>
          </svg>
          Reports
        </button>

        <button 
          className={`settings-tab ${activeTab === 'backup' ? 'active' : ''}`}
          onClick={() => setActiveTab('backup')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10,9 9,9 8,9"></polyline>
          </svg>
          Backup & Restore
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'personal' && renderPersonalDetails()}
        {activeTab === 'artists' && renderArtists()}
        {activeTab === 'venues' && renderVenues()}
        {isAdmin && activeTab === 'users' && renderUsers()}
        {activeTab === 'reports' && (
          <ReportsView reports={reports} summary={summary} />
        )}
        {activeTab === 'backup' && renderBackup()}
      </div>
    </div>
  );
};

const CombinedView = ({ gigs, onEditGig, onArchiveGig, onDeleteGig, onGigClick, onAddGig, onUpdateGig }) => {
  // Show upcoming gigs (future dates) first, then past gigs, limited to 10 total
  const today = new Date().toISOString().split('T')[0];
  const upcomingGigs = gigs.filter(gig => gig.date >= today).sort((a, b) => new Date(a.date) - new Date(b.date));
  const pastGigs = gigs.filter(gig => gig.date < today).sort((a, b) => new Date(b.date) - new Date(a.date));
  const limitedGigs = [...upcomingGigs, ...pastGigs].slice(0, 10);

  return (
    <div className="combined-view">
      <div className="combined-section">
        <h2 className="section-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="section-icon">
            <line x1="8" y1="6" x2="21" y2="6"></line>
            <line x1="8" y1="12" x2="21" y2="12"></line>
            <line x1="8" y1="18" x2="21" y2="18"></line>
            <line x1="3" y1="6" x2="3.01" y2="6"></line>
            <line x1="3" y1="12" x2="3.01" y2="12"></line>
            <line x1="3" y1="18" x2="3.01" y2="18"></line>
          </svg>
          Upcoming Gigs
        </h2>
        <SpreadsheetView 
          gigs={limitedGigs} 
          onEditGig={onEditGig}
          onArchiveGig={onArchiveGig}
          onDeleteGig={onDeleteGig}
          onGigClick={onGigClick}
          onUpdateGig={onUpdateGig}
        />
        {gigs.length > 10 && (
          <div className="view-all-notice">
            <p>Showing {limitedGigs.length} of {gigs.length} gigs. 
               <span className="view-all-link"> View all in Table tab.</span>
            </p>
          </div>
        )}
      </div>
      
      <div className="combined-section">
        <CalendarView gigs={gigs} onEditGig={onEditGig} onAddGig={onAddGig} onUpdateGig={onUpdateGig} />
      </div>
    </div>
  );
};

function GigManagerApp({ onLogout, currentUser }) {
  const [gigs, setGigs] = useState([]);
  const [archivedGigs, setArchivedGigs] = useState([]);
  const [currentView, setCurrentView] = useState('home');
  const [showForm, setShowForm] = useState(false);
  const [editingGig, setEditingGig] = useState(null);
  const [selectedGig, setSelectedGig] = useState(null);
  
  // Helper function to format time from 24-hour to 12-hour with AM/PM (no leading zeros)
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({});
  
  // Admin user creation states
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [newUserData, setNewUserData] = useState({ username: '', password: '' });
  const [adminMessage, setAdminMessage] = useState('');
  const [adminError, setAdminError] = useState('');
  
  // Delete confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [gigToDelete, setGigToDelete] = useState(null);
  
  // Invitation system states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [processingNotification, setProcessingNotification] = useState(null);
  const [friends, setFriends] = useState([]);
  const [artists, setArtists] = useState([]);
  const [venues, setVenues] = useState([]);
  const [userProfile, setUserProfile] = useState({});

  // Check if current user is admin
  const isAdmin = currentUser && ['admin', 'markperic'].includes(currentUser.username);

  // Fetch functions for persistent data
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchArtists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/artists`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setArtists(data);
      }
    } catch (error) {
      console.error('Error fetching artists:', error);
    }
  };

  const fetchVenues = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/venues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setVenues(data);
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
    }
  };

  // Function to get color based on gig status
  const getColorByGigStatus = (gigStatus) => {
    switch (gigStatus) {
      case 'hold': return '#BB0A21';
      case 'confirmed': return '#3FA34D';
      case 'completed': return '#BFB6BB';
      default: return '#BB0A21'; // Default to hold color
    }
  };

  // Helper function to format date as "Oct 7th 2025"
  const formatReadableDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day >= 11 && day <= 13) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';  
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${month} ${day}${getOrdinalSuffix(day)} ${year}`;
  };

  useEffect(() => {
    fetchGigs();
    fetchArchivedGigs();
    fetchReports();
    fetchSummary();
    fetchFriends();
    
    // Load settings data on app initialization
    fetchUserProfile();
    fetchArtists();
    fetchVenues();
    
    // Set basic black theme
    document.documentElement.style.setProperty('--primary-color', '#101010');
    document.documentElement.style.setProperty('--secondary-color', '#1a1a1a');
    document.documentElement.style.setProperty('--accent-color', '#333333');
  }, []);

  // Re-fetch profile when switching to settings view to ensure latest data
  useEffect(() => {
    if (currentView === 'settings') {
      fetchUserProfile();
    }
  }, [currentView]);

  // Admin function to create new users
  const createUser = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminMessage('');
    
    if (!newUserData.username || !newUserData.password) {
      setAdminError('Username and password are required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/create-user`, newUserData);
      setAdminMessage(`User '${newUserData.username}' created successfully!`);
      setNewUserData({ username: '', password: '' });
      setTimeout(() => {
        setShowAdminModal(false);
        setAdminMessage('');
      }, 2000);
    } catch (error) {
      setAdminError(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const fetchGigs = async () => {
    try {
      const response = await axios.get(`${API}/gigs`);
      setGigs(response.data);
    } catch (error) {
      console.error("Error fetching gigs:", error);
    }
  };

  const fetchArchivedGigs = async () => {
    try {
      const response = await axios.get(`${API}/gigs?archived=true`);
      setArchivedGigs(response.data);
    } catch (error) {
      console.error("Error fetching archived gigs:", error);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/monthly`);
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/reports/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingGig(null);
    fetchGigs();
    fetchReports();
    fetchSummary();
  };

  const handleEdit = (gig) => {
    setEditingGig(gig);
    setShowForm(true);
  };

  // Handle adding gig with pre-selected date from calendar
  const handleAddGigWithDate = (selectedDate) => {
    // Format date as YYYY-MM-DD for the form
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Set editing gig with pre-selected date
    setEditingGig({
      date: dateStr,
      time: '',
      end_time: '',
      performance_name: '',
      venue: '',
      performance_fee: '',
      notes: '',
      invoice_status: 'not_generated',
      gig_status: 'hold',
      archived: false
    });
    
    setShowForm(true);
  };

  const handleUpdateGig = async (gigId, updates) => {
    try {
      // If gig_status is being updated, also update the color
      if (updates.gig_status) {
        updates.color = getColorByGigStatus(updates.gig_status);
      }
      
      await axios.put(`${API}/gigs/${gigId}`, updates);
      fetchGigs();
      fetchReports();
      fetchSummary();
    } catch (error) {
      console.error("Error updating gig:", error);
    }
  };

  const handleArchive = async (gigId) => {
    try {
      await axios.put(`${API}/gigs/${gigId}/archive`);
      fetchGigs();
      fetchArchivedGigs();
      fetchSummary();
    } catch (error) {
      console.error("Error archiving gig:", error);
    }
  };

  const handleDelete = async (gigId) => {
    // First find the gig to show details in confirmation
    const gigDetails = gigs.find(g => g.id === gigId);
    setGigToDelete({ id: gigId, details: gigDetails });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/gigs/${gigToDelete.id}`);
      fetchGigs();
      fetchSummary();
      setShowDeleteConfirm(false);
      setGigToDelete(null);
    } catch (error) {
      console.error("Error deleting gig:", error);
      setShowDeleteConfirm(false);
      setGigToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setGigToDelete(null);
  };

  // Google Calendar functions - removed

  const handleUnarchive = async (gigId) => {
    try {
      await axios.put(`${API}/gigs/${gigId}/unarchive`);
      fetchGigs();
      fetchArchivedGigs();
      fetchSummary();
    } catch (error) {
      console.error("Error unarchiving gig:", error);
    }
  };

  const handleGigClick = (gig) => {
    setSelectedGig(gig);
  };

  const closeModal = () => {
    setSelectedGig(null);
  };
  
  // Invitation System Functions
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${API}/invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  };

  const fetchFriends = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/friends`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setFriends(data);
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const handleInviteUser = async (email) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/invite-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email })
      });
      
      if (response.ok) {
        const result = await response.json();
        return { success: true, message: result.message };
      } else {
        const error = await response.json();
        return { success: false, message: error.detail || 'Failed to send invitation' };
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      return { success: false, message: 'Network error occurred' };
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setAvailableUsers([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/friends/search?q=${query}`);
      let filteredUsers = response.data;
      
      // Filter out users who are already collaborators on this gig
      if (selectedGig && selectedGig.collaborators) {
        const existingCollaboratorIds = selectedGig.collaborators.map(c => c.id);
        filteredUsers = filteredUsers.filter(user => !existingCollaboratorIds.includes(user.id));
      }
      
      // TODO: Also filter out users with pending invitations for this gig
      // This would require fetching pending invitations for this gig from backend
      
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error("Error searching users:", error);
    }
  };

  const inviteUsersToGig = async (gigId, userIds) => {
    setInviteLoading(true);
    let successCount = 0;
    let errorMessages = [];
    
    try {
      for (const userId of userIds) {
        try {
          await axios.post(`${API}/gigs/${gigId}/invite`, {
            gig_id: gigId,
            invitee_id: userId
          });
          successCount++;
        } catch (error) {
          const userName = availableUsers.find(u => u.id === userId)?.username || 'Unknown user';
          errorMessages.push(`Failed to invite ${userName}: ${error.response?.data?.detail || error.message}`);
        }
      }
      
      // Clear selections but don't close modal yet
      setSelectedUsers([]);
      setUserSearchQuery('');
      setAvailableUsers([]);
      fetchUnreadCount(); // Refresh notification count
      
      // Improved success/error feedback
      if (successCount === userIds.length) {
        // All invitations sent successfully
        const message = `Successfully invited ${successCount} musician${successCount > 1 ? 's' : ''} to the gig!`;
        setInviteStatus({ type: 'success', message });
        
        // Auto-close modal after 3 seconds on complete success
        setTimeout(() => {
          setShowInviteModal(false);
          setInviteStatus(null);
        }, 3000);
      } else if (successCount > 0) {
        // Partial success - keep modal open
        const message = `Invited ${successCount} out of ${userIds.length} musicians. Some failed:\n${errorMessages.join('\n')}`;
        setInviteStatus({ type: 'warning', message });
      } else {
        // Complete failure - keep modal open
        const message = `Failed to invite musicians:\n${errorMessages.join('\n')}`;
        setInviteStatus({ type: 'error', message });
      }
      
      // Clear status after 8 seconds (longer for modal)
      setTimeout(() => setInviteStatus(null), 8000);
      
    } catch (error) {
      console.error("Error inviting users:", error);
      setInviteStatus({ 
        type: 'error', 
        message: `Error sending invitations: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setInviteStatus(null), 8000);
    } finally {
      setInviteLoading(false);
    }
  };



  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === 'gig_invitation') {
      // For invitation notifications, find the corresponding invitation and show modal
      const invitation = invitations.find(inv => 
        inv.gig?.performance_name === notification.gig_name || 
        notification.message.includes(inv.gig?.performance_name)
      );
      
      if (invitation) {
        setSelectedInvitation(invitation);
        setShowInvitationModal(true);
      } else {
        // Fallback: refresh invitations and show generic message
        fetchInvitations();
        setInviteStatus({ 
          type: 'info', 
          message: '📬 Please check your pending invitations above.' 
        });
        setTimeout(() => setInviteStatus(null), 3000);
      }
    } else if (notification.type === 'gig_invitation_accepted' || 
               notification.type === 'gig_invitation_declined') {
      // These are response notifications - just mark as read, they have dismiss buttons in the dropdown
      markNotificationRead(notification.id);
    } else {
      // Generic notification - just mark as read
      markNotificationRead(notification.id);
    }
  };

  const handleInvitationResponse = async (invitationId, action) => {
    setProcessingNotification(invitationId);
    try {
      if (action === 'accept') {
        const response = await axios.put(`${API}/invitations/${invitationId}/accept`, {}, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setInviteStatus({ 
          type: 'success', 
          message: 'Invitation accepted! The gig has been added to your calendar.' 
        });
      } else if (action === 'decline') {
        const response = await axios.put(`${API}/invitations/${invitationId}/decline`, {}, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setInviteStatus({ 
          type: 'info', 
          message: '📝 Invitation declined.' 
        });
      }
      
      // Refresh data
      fetchInvitations();
      fetchGigs(); // Refresh to show new shared gigs
      fetchNotifications();
      fetchUnreadCount();
      
      // Close modal if open
      if (showInvitationModal) {
        setShowInvitationModal(false);
        setSelectedInvitation(null);
      }
      
      setTimeout(() => setInviteStatus(null), 4000);
      
    } catch (error) {
      console.error(`Error ${action}ing invitation:`, error);
      let errorMessage = `Error ${action}ing invitation.`;
      if (error.response?.data?.detail) {
        errorMessage += ` ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage += ` ${error.message}`;
      }
      setInviteStatus({ 
        type: 'error', 
        message: errorMessage
      });
      setTimeout(() => setInviteStatus(null), 4000);
    } finally {
      setProcessingNotification(null);
    }
  };

  const dismissNotification = async (notificationId) => {
    setProcessingNotification(notificationId);
    try {
      await markNotificationRead(notificationId);
      // Remove from local state immediately for better UX
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    } finally {
      setProcessingNotification(null);
    }
  };
  
  // Fetch notifications and invitations on component mount
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    fetchInvitations();
  }, []);

  return (
    <div className="app">
      <header className="app-header">
        <h1>
          <svg width="24" height="24" viewBox="0 0 3000 3000" fill="none" xmlns="http://www.w3.org/2000/svg" className="header-icon">
            <path d="M2754.9,411.93h-355.94v-121.75c0-114.74-93.34-208.08-208.08-208.08h-111.58c-114.74,0-208.08,93.35-208.08,208.08v121.75h-742.43v-121.75c0-114.74-93.35-208.08-208.08-208.08h-111.58c-114.74,0-208.08,93.35-208.08,208.08v121.75H245.1c-39.01,0-70.74,31.74-70.74,70.74v2230.08c0,39.01,31.73,70.74,70.74,70.74h2509.79c39.01,0,70.74-31.73,70.74-70.74V482.67c0-39.01-31.73-70.74-70.74-70.74ZM809.12,883.99h111.58c114.74,0,208.08-93.34,208.08-208.08v-122.49h742.43v122.49c0,114.74,93.34,208.08,208.08,208.08h111.58c114.74,0,208.08-93.34,208.08-208.08v-122.49h285.19v2088.6H315.84V553.42h285.19v122.49c0,114.74,93.34,208.08,208.08,208.08ZM2079.3,223.59h111.58c36.72,0,66.6,29.88,66.6,66.6v385.72c0,36.72-29.88,66.6-66.6,66.6h-111.58c-36.72,0-66.6-29.88-66.6-66.6v-385.72c0-36.72,29.88-66.6,66.6-66.6ZM809.12,223.59h111.58c36.72,0,66.6,29.88,66.6,66.6v385.72c0,36.72-29.88,66.6-66.6,66.6h-111.58c-36.72,0-66.6-29.88-66.6-66.6v-385.72c0-36.72,29.88-66.6,66.6-66.6Z" fill="currentColor"/>
            <path d="M546.01,2101.99c-23.29,38.99-38.12,80.68-44.08,123.89-6.29,45.56-2.4,90.62,11.55,133.96,13.95,43.33,37.09,82.2,68.77,115.54,30.05,31.62,66.42,56.82,108.08,74.91,46.25,20.08,95.95,30.25,147.75,30.25,41.28,0,82.58-6.56,122.75-19.49,71.76-23.1,134.22-64.72,180.62-120.36,12.18-14.6,23.21-30.14,32.79-46.18,23.29-39,38.12-80.68,44.08-123.89,1.43-10.38,2.34-21.02,2.71-31.63.24-6.82.24-13.68.02-20.39v-686.86l822.96-175.09-.6,518.7-.22,199.44c-2.75,25.98-11.51,51.48-26.05,75.82-29.01,48.58-77.06,85.66-135.3,104.41-25.95,8.36-52.57,12.59-79.12,12.59-44.44,0-86.08-11.69-120.42-33.81-34.02-21.91-58.14-52.57-69.77-88.66-31.62-98.22,37.75-208.73,154.63-246.36,25.43-8.19,51.44-12.45,77.31-12.67h1.04c.35-.02.7-.02,1.05-.02,20.54,0,40.77,2.6,60.14,7.73l43.96,11.65v-417.79c0-39.01-31.74-70.74-70.74-70.74s-70.74,31.73-70.74,70.74v258.72c-28.76,2.76-57.38,8.69-85.37,17.7-90.97,29.29-166.77,88.43-213.41,166.54-23.29,39-38.12,80.68-44.08,123.89-6.29,45.56-2.4,90.63,11.55,133.96,13.95,43.33,37.09,82.2,68.77,115.54,30.05,31.62,66.42,56.82,108.08,74.91,46.25,20.08,95.96,30.25,147.74,30.25,41.28,0,82.58-6.56,122.75-19.49,71.19-22.92,133.29-64.1,179.58-119.1,12.58-14.95,23.96-30.91,33.83-47.43,23.29-39,38.12-80.68,44.08-123.9.76-5.54,1.39-11.18,1.85-16.76,1.34-16.07,1.39-32.3.17-48.26l.87-764.92c.02-21.46-9.53-41.52-26.2-55.03-16.66-13.51-38.26-18.7-59.27-14.24l-964.54,205.21c-32.46,6.91-56.02,36.01-56.02,69.19v761.72c-1.32,29.36-10.34,58.22-26.8,85.78-29.01,48.58-77.06,85.66-135.3,104.41-25.95,8.35-52.57,12.59-79.12,12.59-44.44,0-86.08-11.69-120.42-33.81-34.02-21.91-58.15-52.57-69.77-88.66-31.62-98.22,37.75-208.73,154.63-246.36,26.16-8.42,52.87-12.69,79.4-12.69,2.89,0,6.04.07,9.62.21,21.41.85,42.3,4.52,62.08,10.93l45.78,14.82v-742.37l1130.62-252.84c38.07-8.51,62.11-46.41,53.6-84.48-8.51-38.06-46.41-62.1-84.48-53.6l-1185.92,265.21c-32.56,7.28-55.3,35.67-55.3,69.04v632.35c-33.24,2.04-66.36,8.31-98.75,18.74-90.98,29.29-166.77,88.43-213.42,166.54h0Z" fill="currentColor"/>
          </svg>
          Gig Manager
        </h1>
        <div className="header-actions">
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Gig
          </button>
          
          {/* Notifications Button with Dropdown */}
          <div className="notification-container">
            <button 
              onClick={() => setShowNotifications(!showNotifications)} 
              className="btn btn-secondary notification-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              Notifications
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </button>

            {/* Notification Dropdown - Positioned Here */}
            {showNotifications && (
              <div className="notifications-dropdown">
                <div className="notifications-header">
                  <h4>Notifications</h4>
                  <button 
                    onClick={() => setShowNotifications(false)}
                    className="close-notifications"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="notifications-list">
                  {/* Pending Invitations - Show as actionable items with direct ACCEPT/DECLINE */}
                  {invitations.filter(inv => inv.status === 'pending').length > 0 && (
                    <div className="notification-section">
                      <h5> Gig Invitations</h5>
                      {invitations.filter(inv => inv.status === 'pending').map(invitation => (
                        <div key={invitation.id} className="notification-item invitation-item">
                          <div className="notification-content">
                            <div className="invitation-details">
                              <strong>{invitation.inviter?.username || 'Someone'} invited you to join</strong>
                              <p className="gig-name">{invitation.gig?.performance_name || 'Unknown Gig'}</p>
                              <p className="gig-details">
                                {invitation.gig?.date ? new Date(invitation.gig.date).toLocaleDateString() : 'Unknown Date'}
                                {invitation.gig?.venue && ` • 📍 ${invitation.gig.venue}`}
                              </p>
                            </div>
                          </div>
                          <div className="notification-actions">
                            <button 
                              onClick={() => handleInvitationResponse(invitation.id, 'accept')}
                              disabled={processingNotification === invitation.id}
                              className="btn-mini btn-accept"
                            >
                              {processingNotification === invitation.id ? '⏳' : 'ACCEPT'}
                            </button>
                            <button 
                              onClick={() => handleInvitationResponse(invitation.id, 'decline')}
                              disabled={processingNotification === invitation.id}
                              className="btn-mini btn-decline"
                            >
                              {processingNotification === invitation.id ? '⏳' : 'DECLINE'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Regular Notifications - Show with appropriate action buttons */}
                  {notifications.length > 0 && (
                    <div className="notification-section">
                      <h5>📬 Recent Activity</h5>
                      {notifications.slice(0, 10).map(notification => (
                        <div 
                          key={notification.id} 
                          className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                        >
                          <div className="notification-content">
                            <strong>{notification.title}</strong>
                            <p>{notification.message}</p>
                            <p className="notification-timestamp">
                              {new Date(notification.created_at).toLocaleDateString()} at {new Date(notification.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                          <div className="notification-actions">
                            {notification.type === 'gig_invitation_accepted' || 
                             notification.type === 'gig_invitation_declined' ? (
                              <button 
                                onClick={() => dismissNotification(notification.id)}
                                disabled={processingNotification === notification.id}
                                className="btn-mini btn-dismiss"
                              >
                                {processingNotification === notification.id ? '⏳' : 'DISMISS'}
                              </button>
                            ) : !notification.read ? (
                              <button 
                                onClick={() => markNotificationRead(notification.id)}
                                disabled={processingNotification === notification.id}
                                className="btn-mini btn-mark-read"
                              >
                                {processingNotification === notification.id ? '⏳' : 'MARK READ'}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Empty State */}
                  {invitations.filter(inv => inv.status === 'pending').length === 0 && notifications.length === 0 && (
                    <div className="no-notifications">
                      <p>No notifications</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Admin-only user creation button */}
          {isAdmin && (
            <button onClick={() => setShowAdminModal(true)} className="btn btn-secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <line x1="19" y1="8" x2="19" y2="14"></line>
                <line x1="22" y1="11" x2="16" y2="11"></line>
              </svg>
              Add User
            </button>
          )}

          <button onClick={onLogout} className="btn btn-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16,17 21,12 16,7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Logout
          </button>
        </div>
      </header>

      <nav className="app-nav">
        <button 
          className={`nav-item nav-item-home ${currentView === 'home' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('home')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9,22 9,12 15,12 15,22"></polyline>
          </svg>
          Home
        </button>
        <button 
          className={`nav-item ${currentView === 'spreadsheet' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('spreadsheet')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <line x1="8" y1="6" x2="21" y2="6"></line>
            <line x1="8" y1="12" x2="21" y2="12"></line>
            <line x1="8" y1="18" x2="21" y2="18"></line>
            <line x1="3" y1="6" x2="3.01" y2="6"></line>
            <line x1="3" y1="12" x2="3.01" y2="12"></line>
            <line x1="3" y1="18" x2="3.01" y2="18"></line>
          </svg>
          Table
        </button>
        <button 
          className={`nav-item ${currentView === 'calendar' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('calendar')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          Calendar
        </button>
        <button 
          className={`nav-item ${currentView === 'kanban' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('kanban')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <rect x="7" y="7" width="3" height="9"></rect>
            <rect x="14" y="7" width="3" height="5"></rect>
          </svg>
          Boards
        </button>
        <button 
          className={`nav-item ${currentView === 'friends' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('friends')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
            <circle cx="16" cy="7" r="3"></circle>
          </svg>
          Friends
        </button>
        <button 
          className={`nav-item ${currentView === 'archive' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('archive')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <polyline points="21,8 21,21 3,21 3,8"></polyline>
            <rect x="1" y="3" width="22" height="5"></rect>
            <line x1="10" y1="12" x2="14" y2="12"></line>
          </svg>
          Archive
        </button>
        <button 
          className={`nav-item ${currentView === 'settings' ? 'nav-item-active' : 'nav-item-inactive'}`}
          onClick={() => setCurrentView('settings')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="nav-icon">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
          Settings
        </button>
      </nav>

      <main className="app-main">
        {/* Invite Status Messages */}
        {inviteStatus && (
          <div className={`invite-status-banner ${inviteStatus.type}`}>
            <div className="status-content">
              <span>{inviteStatus.message}</span>
              <button 
                onClick={() => setInviteStatus(null)}
                className="status-close"
              >
                ✕
              </button>
            </div>
          </div>
        )}
        {showForm && (
          <div className="modal">
            <div className="modal-content">
              <GigForm 
                onSave={handleSave} 
                editingGig={editingGig}
                getColorByGigStatus={getColorByGigStatus}
                onArtistsUpdate={fetchArtists}
                onVenuesUpdate={fetchVenues}
                onCancel={() => {
                  setShowForm(false);
                  setEditingGig(null);
                }}
              />
            </div>
          </div>
        )}

        {currentView === 'home' && (
          <CombinedView 
            gigs={gigs} 
            onEditGig={handleEdit}
            onArchiveGig={handleArchive}
            onDeleteGig={handleDelete}
            onGigClick={handleGigClick}
            onAddGig={handleAddGigWithDate}
            onUpdateGig={handleUpdateGig}
          />
        )}
        {currentView === 'calendar' && <CalendarView gigs={gigs} onEditGig={handleEdit} onAddGig={handleAddGigWithDate} onUpdateGig={handleUpdateGig} onShowInviteModal={(gig) => {
          setSelectedGig(gig);
          setShowInviteModal(true);
        }} />}
        {currentView === 'kanban' && (
          <KanbanView 
            gigs={gigs} 
            onUpdateGig={handleUpdateGig}
            onGigClick={handleGigClick} 
          />
        )}
        {currentView === 'spreadsheet' && (
          <SpreadsheetView 
            gigs={gigs} 
            onEditGig={handleEdit}
            onArchiveGig={handleArchive}
            onDeleteGig={handleDelete}
            onGigClick={handleGigClick}
            onUpdateGig={handleUpdateGig}
          />
        )}

        {currentView === 'archive' && (
          <div className="archive-view">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}>
                <polyline points="21,8 21,21 3,21 3,8"></polyline>
                <rect x="1" y="3" width="22" height="5"></rect>
                <line x1="10" y1="12" x2="14" y2="12"></line>
              </svg>
              Archived Gigs
            </h2>
            <table className="gigs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Performance</th>
                  <th>Venue</th>
                  <th>Fee</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {archivedGigs.map(gig => (
                  <tr key={gig.id}>
                    <td>{formatReadableDate(gig.date)}</td>
                    <td>{gig.performance_name}</td>
                    <td>{gig.venue}</td>
                    <td>${gig.performance_fee}</td>
                    <td>
                      <button onClick={() => handleUnarchive(gig.id)} className="btn btn-sm">
                        Unarchive
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {currentView === 'friends' && (
          <div className="friends-view">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <circle cx="16" cy="7" r="3"></circle>
              </svg>
              Friends & Band Members
            </h2>
            <FriendsTab 
              friends={friends} 
              onFriendsUpdate={fetchFriends}
              onInviteUser={handleInviteUser}
            />
          </div>
        )}
        {currentView === 'settings' && (
          <SettingsView 
            userProfile={userProfile}
            artists={artists}
            venues={venues}
            reports={reports}
            summary={summary}
            onUserProfileUpdate={fetchUserProfile}
            onArtistsUpdate={fetchArtists}
            onVenuesUpdate={fetchVenues}
            currentUser={currentUser}
          />
        )}
      </main>

      {/* Gig Detail Modal */}
      {selectedGig && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content gig-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gig-detail-header" style={{ borderColor: selectedGig.color || '#667eea' }}>
              <div className="gig-detail-date">
                {new Date(selectedGig.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              <div className="gig-detail-time">{formatTime(selectedGig.time)}{selectedGig.end_time && ` - ${formatTime(selectedGig.end_time)}`}</div>
            </div>
            
            <div className="gig-detail-body">
              <h3 className="gig-detail-title">{selectedGig.performance_name}</h3>
              
              <div className="gig-detail-info">
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span className="gig-detail-label">Venue:</span>
                  <span>{selectedGig.venue}</span>
                </div>
                
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <span className="gig-detail-label">Fee:</span>
                  <span>${selectedGig.performance_fee}</span>
                </div>
                
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span className="gig-detail-label">Gig Status:</span>
                  <select
                    value={selectedGig.gig_status || "hold"}
                    onChange={(e) => {
                      const updatedGig = { ...selectedGig, gig_status: e.target.value };
                      setSelectedGig(updatedGig);
                      handleUpdateGig(selectedGig.id, { gig_status: e.target.value });
                    }}
                    className="status-select inline-status-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="hold">Hold</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="gig-detail-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10,9 9,9 8,9"></polyline>
                  </svg>
                  <span className="gig-detail-label">Invoice Status:</span>
                  <select
                    value={selectedGig.invoice_status || "not_generated"}
                    onChange={(e) => {
                      const updatedGig = { ...selectedGig, invoice_status: e.target.value };
                      setSelectedGig(updatedGig);
                      handleUpdateGig(selectedGig.id, { invoice_status: e.target.value });
                    }}
                    className="status-select inline-status-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="not_generated">Not Generated</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              
              {selectedGig.notes && (
                <div className="gig-detail-notes">
                  <div className="notes-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                    </svg>
                    <span className="gig-detail-label">Notes:</span>
                  </div>
                  <p>{selectedGig.notes}</p>
                </div>
              )}
              
              {/* Collaborators Section */}
              {selectedGig.collaborators && selectedGig.collaborators.length > 0 && (
                <div className="gig-collaborators-section">
                  <div className="collaborators-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <circle cx="16" cy="7" r="3"></circle>
                    </svg>
                    <span className="gig-detail-label">Band Members:</span>
                  </div>
                  <div className="collaborators-list">
                    {selectedGig.collaborators.map((collaborator, index) => (
                      <div key={collaborator.id} className="collaborator-item">
                        <div className="collaborator-avatar">
                          {collaborator.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="collaborator-info">
                          <span className="collaborator-name">{collaborator.username}</span>
                          <span className={`collaborator-role ${collaborator.role}`}>
                            {collaborator.role === 'owner' ? 'Owner' : 'Collaborator'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <BandMemberDisplay 
                gigId={selectedGig.id}
                bandMembers={selectedGig.band_members || []}
                onUpdate={() => {
                  // Just close the selector - no need to reload the entire page
                  console.log('Band members updated successfully');
                }}
              />
              
              {/* Invite Musicians Section */}
              {(!selectedGig.is_shared || selectedGig.user_permission === 'owner') && (
                <div className="invite-musicians-section">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowInviteModal(true);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowInviteModal(true);
                    }}
                    className="btn btn-outline invite-btn mobile-touch-btn"
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                    Invite Musicians
                  </button>
                </div>
              )}
              
              {/* Shared Gig Indicator */}
              {selectedGig.is_shared && (
                <div className="shared-gig-indicator">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  <span>Shared Gig - View Only</span>
                </div>
              )}
            </div>
            
            <div className="gig-detail-actions">
              <button onClick={() => { setEditingGig(selectedGig); setShowForm(true); closeModal(); }} className="btn btn-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit Gig
              </button>
              <button onClick={closeModal} className="btn btn-secondary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Modal for Creating Users */}
      {showAdminModal && (
        <div className="modal" onClick={() => setShowAdminModal(false)}>
          <div className="modal-content admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Add New User</h3>
              <p>Create a new account for someone to access the gig manager</p>
            </div>
            
            <form onSubmit={createUser} className="admin-form">
              {adminError && <div className="error-message">{adminError}</div>}
              {adminMessage && <div className="success-message">{adminMessage}</div>}
              
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={newUserData.username}
                  onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                  placeholder="Enter username"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  placeholder="Enter password"
                  required
                />
              </div>
              
              <div className="admin-modal-actions">
                <button type="submit" className="btn btn-primary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="23" y1="11" x2="17" y2="11"></line>
                  </svg>
                  Create User
                </button>
                <button type="button" onClick={() => setShowAdminModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && gigToDelete && (
        <div className="modal" onClick={cancelDelete}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <div className="delete-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <h3>Delete Gig</h3>
              <p>Are you sure you want to permanently delete this gig?</p>
            </div>
            
            {gigToDelete.details && (
              <div className="delete-gig-details">
                <div className="detail-row">
                  <strong>Performance:</strong> {gigToDelete.details.performance_name}
                </div>
                <div className="detail-row">
                  <strong>Date:</strong> {new Date(gigToDelete.details.date).toLocaleDateString()}
                </div>
                <div className="detail-row">
                  <strong>Venue:</strong> {gigToDelete.details.venue}
                </div>
                <div className="detail-row">
                  <strong>Fee:</strong> ${gigToDelete.details.performance_fee}
                </div>
              </div>
            )}
            
            <div className="delete-confirm-actions">
              <button onClick={confirmDelete} className="btn btn-danger delete-confirm-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
                Yes, Delete
              </button>
              <button onClick={cancelDelete} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Invitation Response Modal */}
      {showInvitationModal && selectedInvitation && (
        <div className="modal" onClick={() => setShowInvitationModal(false)}>
          <div className="modal-content invitation-response-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invitation-modal-header">
              <h3> Gig Invitation</h3>
            </div>
            
            <div className="invitation-modal-body">
              <div className="invitation-details-full">
                <div className="inviter-info">
                  <strong>{selectedInvitation.inviter?.username || 'Someone'}</strong> has invited you to join:
                </div>
                
                <div className="gig-info-card">
                  <h4>{selectedInvitation.gig?.performance_name || 'Unknown Gig'}</h4>
                  
                  <div className="gig-details-grid">
                    <div className="detail-item">
                      <span className="label">Date:</span>
                      <span className="value">
                        {selectedInvitation.gig?.date ? 
                          new Date(selectedInvitation.gig.date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'Unknown Date'
                        }
                      </span>
                    </div>
                    
                    {selectedInvitation.gig?.time && (
                      <div className="detail-item">
                        <span className="label">🕐 Time:</span>
                        <span className="value">{selectedInvitation.gig.time}</span>
                      </div>
                    )}
                    
                    {selectedInvitation.gig?.venue && (
                      <div className="detail-item">
                        <span className="label">📍 Venue:</span>
                        <span className="value">{selectedInvitation.gig.venue}</span>
                      </div>
                    )}
                    
                    {selectedInvitation.gig?.performance_fee && (
                      <div className="detail-item">
                        <span className="label">💰 Fee:</span>
                        <span className="value">${selectedInvitation.gig.performance_fee}</span>
                      </div>
                    )}
                    
                    {selectedInvitation.gig?.notes && (
                      <div className="detail-item full-width">
                        <span className="label">📝 Notes:</span>
                        <span className="value">{selectedInvitation.gig.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="invitation-modal-actions">
              <button 
                onClick={() => handleInvitationResponse(selectedInvitation.id, 'accept')}
                disabled={processingNotification === selectedInvitation.id}
                className="btn btn-success btn-large"
              >
                {processingNotification === selectedInvitation.id ? (
                  <>
                    <div className="spinner-small"></div>
                    Accepting...
                  </>
                ) : (
                  <>
                    Accept Invitation
                  </>
                )}
              </button>
              
              <button 
                onClick={() => handleInvitationResponse(selectedInvitation.id, 'decline')}
                disabled={processingNotification === selectedInvitation.id}
                className="btn btn-decline btn-large"
              >
                {processingNotification === selectedInvitation.id ? (
                  <>
                    <div className="spinner-small"></div>
                    Declining...
                  </>
                ) : (
                  <>
                    Decline Invitation
                  </>
                )}
              </button>
              
              <button 
                onClick={() => setShowInvitationModal(false)}
                disabled={processingNotification === selectedInvitation.id}
                className="btn btn-secondary btn-large"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Musicians Modal */}
      {showInviteModal && selectedGig && (
        <div className="modal" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invite-modal-header">
              <h3>Invite Musicians to Gig</h3>
              <p>Invite registered users to perform at "{selectedGig.performance_name}"</p>
            </div>

            {/* Invite Status in Modal */}
            {inviteStatus && (
              <div className={`invite-modal-status ${inviteStatus.type}`}>
                <div className="status-content">
                  <span>{inviteStatus.message}</span>
                  <button 
                    onClick={() => setInviteStatus(null)}
                    className="status-close-small"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
            
            <div className="invite-form">
              <div className="form-group">
                <label>Search Musicians</label>
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  placeholder="Type to search for musicians..."
                  className="search-input"
                />
              </div>
              
              {availableUsers.length > 0 && (
                <div className="available-users">
                  <h4>Available Musicians:</h4>
                  <div className="users-list">
                    {availableUsers.map(user => (
                      <div 
                        key={user.id} 
                        className={`user-item ${selectedUsers.includes(user.id) ? 'selected' : ''}`}
                        onClick={() => {
                          if (selectedUsers.includes(user.id)) {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                          } else {
                            setSelectedUsers([...selectedUsers, user.id]);
                          }
                        }}
                      >
                        <div className="user-info">
                          <span className="username">{user.username}</span>
                        </div>
                        <div className="select-indicator">
                          {selectedUsers.includes(user.id) && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 6L9 17l-5-5"></path>
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* No available users message */}
              {userSearchQuery.length >= 2 && availableUsers.length === 0 && (
                <div className="no-available-users">
                  <p>No available musicians found matching "{userSearchQuery}".</p>
                  {selectedGig && selectedGig.collaborators && selectedGig.collaborators.length > 1 && (
                    <p className="help-text">Musicians already on this gig are automatically filtered out.</p>
                  )}
                </div>
              )}
              
              {selectedUsers.length > 0 && (
                <div className="selected-users">
                  <h4>Selected Musicians: {selectedUsers.length}</h4>
                </div>
              )}
            </div>
            
            <div className="invite-modal-actions">
              <button 
                onClick={() => inviteUsersToGig(selectedGig.id, selectedUsers)}
                disabled={selectedUsers.length === 0 || inviteLoading}
                className="btn btn-primary"
              >
                {inviteLoading ? 'Sending...' : `Invite ${selectedUsers.length} Musician${selectedUsers.length !== 1 ? 's' : ''}`}
              </button>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      

    </div>
  );
}

// Main App Component with Authentication
function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

// Component that shows login or main app based on auth status
const AuthenticatedApp = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <LoginForm />;
  }
  
  return <ProtectedApp />;
};

export default App;